"use strict";

import ahk from "../antihackkey.json";
import mapCatalog from "../public/maps.json";
import version from "../version.json";
import builtInMapsFactory from "../src/data/maps.js";
import { resetConnectState, setConnectTargetState } from "./connectState";
import { getCurrentOriginalMapData } from "./mapStore";
import { buildGeneratedNavMesh } from "../shared/navgen";

type AnyRecord = Record<string, any>;
type BotDifficultyMode = "easy" | "normal" | "hard" | "max";
const BOT_DIFFICULTY_PRESETS: Record<BotDifficultyMode, number> = {
  easy: 0.65,
  normal: 1,
  hard: 1.8,
  max: 3,
};
type BotAIConfig = {
  difficulty: number;
  accuracy: number;
  targetDistance: number;
  attackTarget: boolean;
  followTarget: boolean;
  watchTarget: boolean;
  roam: boolean;
  randomJump: boolean;
  slideHop: boolean;
  forceCrouch: boolean;
  targetTeam: boolean;
  autoRespawn: boolean;
  useSpawnDelay: boolean;
  respawnDelay: number;
};
type BotAIFrameSnapshot = {
  frameId: number;
  delta: number;
  pitch: number;
  yaw: number;
  fields: Record<number, number>;
};
type NavOverlayPoint = { x: number; y: number; z: number };
type EntBBoxMode = "look" | "all" | "players" | "objects" | "filter";
type EntBBoxTarget = {
  label: string;
  kind: "player" | "object";
  ref: AnyRecord;
  object3D: AnyRecord | null;
};
const DEFAULT_AI_CONFIG: BotAIConfig = {
  difficulty: BOT_DIFFICULTY_PRESETS.max,
  accuracy: 100,
  targetDistance: 5000,
  attackTarget: true,
  followTarget: true,
  watchTarget: false,
  roam: true,
  randomJump: false,
  slideHop: false,
  forceCrouch: false,
  targetTeam: false,
  autoRespawn: true,
  useSpawnDelay: false,
  respawnDelay: 0,
};
const OFFLINE_BOT_CLANS = ["StaZ", "X", "EMPY", "Sour", "KRM1", "SHOT", "HIZ", "RGB", "HALO", "GLXY", "SWEL", "Oras", "PNTM", "THIC"];

type CommandArgType = "string" | "number" | "boolean";
type CommandArg = {
  name: string;
  type: CommandArgType;
  optional: boolean;
};
type CommandRun = (args: string[], rest: string) => void;
type LocalCommand = {
  kind: "local";
  args: CommandArg[];
  desc: string;
  run: CommandRun;
};
type ServerCommand = {
  kind: "server";
  args: CommandArg[];
  desc: string;
};
type Command = LocalCommand | ServerCommand;
type GameLike = Krunker.Game & AnyRecord;
type ModeEntry = {
  index: number;
  mode: AnyRecord;
  alias: string;
  name: string;
};
type ClassEntry = {
  index: number;
  classConfig: AnyRecord;
  name: string;
  primaryName: string;
};
type OfflineMapEntry = {
  index: number;
  map: AnyRecord;
  name: string;
  id: string;
  fileName: string;
  custom: boolean;
};
const CUSTOM_MAP_ID_START = 9000;
const OFFLINE_SANDBOX_MAP_INDEX_KEY = "__devConsoleMapIndex";
let activeOfflineSandboxMapEntry: OfflineMapEntry | null = null;
type BotType = "nextbot" | "tfue";
type ResolveReason =
  | "ambiguous"
  | "empty"
  | "no-maps"
  | "no-modes"
  | "not-found"
  | null;
type IndexResolve<TExtra extends AnyRecord = AnyRecord> = TExtra & {
  index: number | null;
  reason: ResolveReason;
};
type AIEntity = AnyRecord & {
  active?: boolean;
  kill?: (source?: Krunker.Player | null) => void;
  name?: string;
  sid?: number | string;
};
type OfflineKillResolve =
  | { kind: "player"; target: Krunker.Player; reason: null }
  | { kind: "ai"; target: AIEntity; reason: null }
  | { kind: null; target: null; reason: "ambiguous" | "empty" | "not-found" };
type OfflineBotGame = GameLike & {
  __botType?: BotType;
  __botManagers?: Partial<Record<BotType, any>>;
};
type ForceCollideObject = AnyRecord & {
  collidable?: boolean;
  meshRef?: AnyRecord & { collidable?: boolean };
  noCol?: boolean;
};
type ForceCollideInjectedObject = ForceCollideObject & {
  __forceColInjected: true;
  __forceColSourceIndex: number;
};
type ForceCollideSourceObject = AnyRecord & {
  col?: unknown;
  i?: unknown;
  id?: unknown;
  l?: unknown;
  p?: unknown;
  s?: unknown;
};
type ForceCollideEntry = {
  obj: ForceCollideObject;
  hasNoCol: boolean;
  noCol: boolean | undefined;
  hasCollidable: boolean;
  collidable: boolean | undefined;
  hasMeshCollidable: boolean;
  meshCollidable: boolean | undefined;
};
type ForceCollideState = {
  enabled: boolean;
  entries: ForceCollideEntry[];
  injected: ForceCollideInjectedObject[];
};
type ConsoleGeom = {
  left?: number;
  top?: number;
  width?: number;
  height?: number;
};
type WrappedHandler = ((...args: any[]) => any) & {
  __devConsoleWrapped?: boolean;
};
type ConsoleFragment = {
  text: string;
  style?: string;
};
type DevConsolePacket = {
  kind?: ConsoleKind;
  args?: unknown[];
};

// Source-engine style developer console for Krunker.
// ─────────────────────────────────────────────────────────
// • `~` (handled in cheats.js) toggles visibility.
// • Type a command, press Enter. Up/Down navigates history.
// • Tab completes the current token.
// • An argument hint row renders under the input as you type
//   (mimicking Source's auto-suggest with `number?` `boolean?`).
// • Output area mirrors console.log/warn/error and incoming
//   chat (`ch` packets). Submitted commands echo as `] <cmd>`.
//
// Commands either run locally (registered via `registerLocal`)
// or are forwarded to the server as `/cmd args` over the chat
// packet (`io.send("ct", "/cmd args")`). The server already
// recognises a large set of slash commands in
// server/packetHandlers.ts — those are mirrored here so the
// console can advertise them in autocomplete/help.
// ─────────────────────────────────────────────────────────

// ─── DOM scaffolding ────────────────────────────────────

// Inject a stylesheet so we can use scoped selectors (scrollbar,
// pseudo-elements, hover states) — inline styles can't do those.
const style = document.createElement("style");
style.textContent = `
@font-face {
  font-family: "LuconDevConsole";
  src: url("/css/fonts/lucon.ttf") format("truetype");
  font-display: block;
}
#devConsoleOverlay {
  position: fixed;
  inset: 0;
  display: none;
  z-index: 999999;
  background: transparent;
  pointer-events: none;
}
#devConsoleOverlay * {
  font-family: "LuconDevConsole", "Lucida Console", "Consolas", "Liberation Mono", "Courier New", monospace;
  -webkit-font-smoothing: none;
  -moz-osx-font-smoothing: grayscale;
  font-smooth: never;
  text-rendering: geometricPrecision;
}
#devConsoleOverlay .modal {
  opacity: 0.95;
  position: absolute;
  right: 8px;
  top: 8px;
  box-sizing: border-box;
  padding: 7px;
  border-radius: 5px;
  width: min(744px, calc(100vw - 16px));
  height: min(280px, calc(100vh - 16px));
  background: #7a7a7a;
  display: flex;
  flex-direction: column;
  color: #d0d0d0;
  font-size: 12px;
  pointer-events: auto;
  min-width: 280px;
  min-height: 160px;
}
#devConsoleOverlay .titlebar {
  background: #7a7a7a;
  font-family: inherit;
  font-size: 10px;
  font-weight: normal;
  padding: 3px 6px;
  user-select: none;
  cursor: move;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 18px;
  flex: 0 0 auto;
}
#devConsoleOverlay .titlebar span {
  color: #fff;
  font-weight: bold;
}
#devConsoleOverlay .titlebar .x {
  width: 18px;
  height: 16px;
  background: transparent;
  border: 0;
  color: #d0d0d0;
  font-family: inherit;
  font-size: 10px;
  line-height: 14px;
  text-align: center;
  cursor: pointer;
  user-select: none;
}
#devConsoleOverlay .titlebar .x:hover { color: #fff; }
#devConsoleOverlay .body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding: 6px 6px 5px;
  gap: 2px;
  overflow: hidden;
  background: #7a7a7a;
}
#devConsoleOverlay .logs {
  flex: 1 1 auto;
  overflow-y: scroll;
  background: #6a6a6a;
  border: 1px #282828 inset;
  padding: 3px 5px;
  white-space: pre-wrap;
  word-break: break-word;
  color: #e0e0e0;
  line-height: 1.25;
  font-family: inherit;
  font-size: 12px;
}

#devConsoleOverlay .logs, #devConsoleOverlay .logs * {
  user-select: text !important;
  -webkit-user-select: text !important;
  cursor: text;
}

#devConsoleOverlay .logs ::selection,
#devConsoleOverlay .logs::selection {
  background: #f39c00;
  color: #000000;
}
#devConsoleOverlay .logs::-webkit-scrollbar { width: 14px; }
#devConsoleOverlay .logs::-webkit-scrollbar-track {
  background: #4a4a4a;
}
#devConsoleOverlay .logs::-webkit-scrollbar-thumb {
  background: #7a7a7a;
  border: 1px solid #3a3a3a;
}
#devConsoleOverlay .hints {
  position: absolute;
  left: 0;
  right: 70px;
  bottom: calc(100% + 1px);
  display: none;
  max-height: 180px;
  overflow-y: auto;
  background: #6f6f6f;
  border: 1px solid #4c4c4c;
  color: #d8d8d8;
  font-size: 11px;
  line-height: 1.2;
  white-space: nowrap;
  z-index: 2;
}
#devConsoleOverlay .hints.isOpen { display: block; }
#devConsoleOverlay .hintItem {
  padding: 2px 6px;
  cursor: default;
}
#devConsoleOverlay .hintItem.isActive {
  background: #5f5f5f;
  color: #ffffff;
}
#devConsoleOverlay .hintMeta {
  color: #b8b8b8;
}
#devConsoleOverlay .inputShell {
  position: relative;
  flex: 0 0 auto;
  margin-top: 1px;
}
#devConsoleOverlay .row {
  display: flex;
  gap: 6px;
  align-items: stretch;
}
#devConsoleOverlay .row input {
  flex: 1 1 auto;
  background: #585858;
  outline: 0;
  color: #e0e0e0;
  font-family: inherit;
  font-size: 12px;
  padding: 3px 5px;
  min-width: 0;
  border: 1px #282828 inset;
}
#devConsoleOverlay .row button[type="submit"] {
  background-color: #7a7a7a;
  width: 64px;
  border: 1px #a2a2a2 outset;
  font-size: 10px;
}

#devConsoleOverlay .row button {
  background: #7a7a7a;
  border: 1px solid #3a3a3a;
  font-family: inherit;
  font-size: 12px;
  padding: 3px 0;
  cursor: pointer;
  color: #e0e0e0;
}
#devConsoleOverlay .row button:hover { background: #8a8a8a; }
#devConsoleOverlay .row button:active { background: #6a6a6a; }
#devConsoleOverlay .grip {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: nwse-resize;
  background:
    linear-gradient(135deg, transparent 0 6px, #9a9a9a 6px 7px, transparent 7px 9px, #9a9a9a 9px 10px, transparent 10px 12px, #9a9a9a 12px 13px, transparent 13px);
}
`;
(document.head || document.documentElement).appendChild(style);

const devOverlay = document.createElement("div");
devOverlay.id = "devConsoleOverlay";

const modal = document.createElement("div");
modal.className = "modal";

const header = document.createElement("div");
header.className = "titlebar";
const headerLabel = document.createElement("span");
headerLabel.textContent = "Console";
const closeBtn = document.createElement("span");
closeBtn.className = "x";
closeBtn.textContent = "✕";
closeBtn.addEventListener("click", () => toggleConsole(false));
header.append(headerLabel, closeBtn);

const body = document.createElement("div");
body.className = "body";

const logs = document.createElement("div");
logs.className = "logs";
let logsAutoScroll = true;
let logsScrollFrame = 0;
let logsForcePinnedFrames = 0;

function logsAtBottom(): boolean {
  return logs.scrollTop + logs.clientHeight >= logs.scrollHeight - 4;
}

function keepLogsAtBottom(): void {
  if (logsScrollFrame) cancelAnimationFrame(logsScrollFrame);
  logsScrollFrame = requestAnimationFrame(() => {
    logsScrollFrame = 0;
    if (!logsAutoScroll) return;
    logs.scrollTop = logs.scrollHeight;
    if (logsForcePinnedFrames > 0) {
      logsForcePinnedFrames--;
      keepLogsAtBottom();
    }
  });
}

logs.addEventListener("scroll", () => {
  if (logsForcePinnedFrames > 0) return;
  logsAutoScroll = logsAtBottom();
});

function keepLogsAtBottomAfterResize(): void {
  if (!logsAutoScroll) return;
  logsForcePinnedFrames = 2;
  keepLogsAtBottom();
}

function enableLogsAutoScroll(): void {
  logsAutoScroll = true;
  logsForcePinnedFrames = 2;
  keepLogsAtBottom();
}

if (typeof ResizeObserver !== "undefined") {
  new ResizeObserver(() => {
    keepLogsAtBottomAfterResize();
  }).observe(logs);
}
// Make logs focusable so Ctrl+A inside it can be scoped to just the logs.
logs.tabIndex = -1;
logs.style.outline = "none";
logs.addEventListener("mousedown", () => {
  // Focus on click so subsequent keydown is targeted at logs.
  setTimeout(() => logs.focus({ preventScroll: true }), 0);
});
logs.addEventListener("keydown", (e) => {
  if ((e.ctrlKey || e.metaKey) && (e.key === "a" || e.key === "A")) {
    e.preventDefault();
    e.stopPropagation();
    const sel = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(logs);
    sel.removeAllRanges();
    sel.addRange(range);
  }
});

const hints = document.createElement("div");
hints.className = "hints";

const inputShell = document.createElement("div");
inputShell.className = "inputShell";

// Use a <form> so pressing Enter inside the input dispatches a
// `submit` event — this fires even if Krunker's engine installed a
// capture-phase keydown listener that calls stopImmediatePropagation
// on Enter (which would otherwise prevent our own keydown handler
// from ever seeing it).
const inputRow = document.createElement("form");
inputRow.className = "row";

const input = document.createElement("input");
input.type = "text";
input.autocomplete = "off";
input.spellcheck = false;

const submitBtn = document.createElement("button");
submitBtn.type = "submit";
submitBtn.textContent = "Submit";

const grip = document.createElement("div");
grip.className = "grip";

inputRow.append(input, submitBtn);
inputShell.append(hints, inputRow);
body.append(logs, inputShell);
modal.append(header, body, grip);
devOverlay.append(modal);

// Defer attaching until <body> exists (this file is required during
// hook bootstrap, possibly before DOMContentLoaded).
const attach = (): HTMLElement | false =>
  document.body && document.body.appendChild(devOverlay);
if (document.body) attach();
else document.addEventListener("DOMContentLoaded", attach, { once: true });

// ─── Output sink ────────────────────────────────────────

// Source/HL2 console palette: white background, black body text,
// red for warnings/errors, dim gray for hints, dark for echoed cmds.
const COLORS = {
  log: "#e0e0e0",
  info: "#9ac8ff",
  warn: "#ffb84d",
  error: "#ff6a6a",
  cmd: "#ffffff",
  reply: "#9affb0",
  hint: "#b0b0b0",
};
type ConsoleKind = keyof typeof COLORS;

function appendLine(
  content: string | ConsoleFragment[],
  kind: ConsoleKind = "log",
): void {
  const line = document.createElement("div");
  line.style.color = COLORS[kind] || COLORS.log;
  if (typeof content === "string") {
    line.textContent = truncateConsoleDataUris(content);
  } else {
    for (const part of content) {
      const span = document.createElement("span");
      span.textContent = truncateConsoleDataUris(part.text);
      if (part.style) span.style.cssText = part.style;
      line.appendChild(span);
    }
  }
  const shouldAutoScroll = logsAutoScroll || logsAtBottom();
  logs.appendChild(line);
  while (logs.childElementCount > 1000) logs.removeChild(logs.firstChild);
  if (shouldAutoScroll) {
    logsAutoScroll = true;
    keepLogsAtBottom();
  }
}

function append(text: unknown, kind: ConsoleKind = "log"): void {
  appendLine(String(text), kind);
}

const CONSOLE_MAX_STRING = 2000;
const CONSOLE_MAX_DATA_URI = 128;
const CONSOLE_MAX_KEYS = 20;
const CONSOLE_MAX_ITEMS = 30;
const CONSOLE_MAX_DEPTH = 2;
const CONSOLE_DATA_URI_RE = /\bdata:[^\s"'<>]+/gi;

function truncateConsoleDataUris(text: string): string {
  return text.replace(CONSOLE_DATA_URI_RE, (uri) =>
    uri.length > CONSOLE_MAX_DATA_URI
      ? uri.slice(0, CONSOLE_MAX_DATA_URI - 3) + "..."
      : uri,
  );
}

function truncateConsoleString(text: string, limit = CONSOLE_MAX_STRING): string {
  text = truncateConsoleDataUris(text);
  return text.length > limit ? text.slice(0, limit) + `...<${text.length - limit} more>` : text;
}

function summarizeConsoleArg(
  value: unknown,
  depth = 0,
  seen = new WeakSet<object>(),
): unknown {
  if (value instanceof Error) return value.stack || `${value.name}: ${value.message}`;
  if (typeof value === "string") return truncateConsoleString(value);
  if (value == null || typeof value !== "object") return value;
  if (seen.has(value)) return "[Circular]";
  if (typeof Node !== "undefined" && value instanceof Node) {
    return `[${value.nodeName}${value instanceof HTMLElement && value.id ? `#${value.id}` : ""}]`;
  }
  const name = value.constructor?.name || "Object";
  if (depth >= CONSOLE_MAX_DEPTH) return `[${name}]`;

  seen.add(value);
  if (Array.isArray(value)) {
    const out = value
      .slice(0, CONSOLE_MAX_ITEMS)
      .map((item) => summarizeConsoleArg(item, depth + 1, seen));
    if (value.length > CONSOLE_MAX_ITEMS)
      out.push(`...${value.length - CONSOLE_MAX_ITEMS} more`);
    return out;
  }

  const out: Record<string, unknown> = {};
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);
  for (const key of keys.slice(0, CONSOLE_MAX_KEYS)) {
    out[key] = summarizeConsoleArg(record[key], depth + 1, seen);
  }
  if (keys.length > CONSOLE_MAX_KEYS) out.__truncatedKeys = keys.length - CONSOLE_MAX_KEYS;
  return out;
}

function stringifyConsoleArg(value: unknown): string {
  const summarized = summarizeConsoleArg(value);
  if (typeof summarized === "string") return summarized;
  try {
    const json = JSON.stringify(summarized);
    if (json !== undefined) return json;
  } catch {}
  return String(summarized);
}

function pushConsoleFragment(
  parts: ConsoleFragment[],
  text: string,
  style?: string,
): void {
  if (!text) return;
  const prev = parts[parts.length - 1];
  if (prev && prev.style === style) {
    prev.text += text;
    return;
  }
  parts.push({ text, style });
}

function formatConsoleArgs(args: unknown[]): string | ConsoleFragment[] {
  if (!args.length) return "";
  if (typeof args[0] !== "string") {
    return args.map(stringifyConsoleArg).join(" ");
  }

  const format = args[0];
  let argIndex = 1;
  let activeStyle = "";
  const parts: ConsoleFragment[] = [];

  for (let i = 0; i < format.length; i++) {
    const ch = format[i];
    if (ch !== "%" || i === format.length - 1) {
      pushConsoleFragment(parts, ch, activeStyle || undefined);
      continue;
    }

    const token = format[++i];
    if (token === "%") {
      pushConsoleFragment(parts, "%", activeStyle || undefined);
      continue;
    }
    if (token === "c") {
      activeStyle = argIndex < args.length ? String(args[argIndex++] || "") : "";
      continue;
    }
    if ("sdifoO".includes(token)) {
      const value = argIndex < args.length ? stringifyConsoleArg(args[argIndex++]) : `%${token}`;
      pushConsoleFragment(parts, value, activeStyle || undefined);
      continue;
    }

    pushConsoleFragment(parts, `%${token}`, activeStyle || undefined);
  }

  if (argIndex < args.length) {
    const rest = args.slice(argIndex).map(stringifyConsoleArg).join(" ");
    pushConsoleFragment(parts, (parts.length ? " " : "") + rest);
  }

  return parts.some((part) => part.style) ? parts : parts.map((part) => part.text).join("");
}

export const print = (text: unknown): void => append(text, "log");

// Mirror console.* — keep originals intact for devtools.
for (const k of ["log", "info", "warn", "error"] as const) {
  const orig = console[k].bind(console) as (...args: unknown[]) => void;
  console[k] = function (...args: unknown[]): void {
    try {
      appendLine(formatConsoleArgs(args), k);
    } catch {
      /* never let logging crash the game */
    }
    orig(...args);
  } as Console[typeof k];
}

// ─── Command registry ───────────────────────────────────

const cmds: Record<string, Command> = Object.create(null);

function arg(
  name: string,
  type: CommandArgType = "string",
  optional = false,
): CommandArg {
  return { name, type, optional };
}

function registerLocal(
  name: string,
  args: CommandArg[],
  desc: string,
  run: CommandRun,
): void {
  cmds[name] = { kind: "local", args, desc, run };
}

function registerServer(name: string, args: CommandArg[], desc: string): void {
  cmds[name] = { kind: "server", args, desc };
}

// ── Local commands ─────────────────────────────────────

registerLocal("help", [arg("cmd", "string", true)], "list commands", (a) => {
  if (a[0]) {
    const c = cmds[a[0]];
    if (!c) return append(`unknown command: ${a[0]}`, "error");
    append(`${a[0]} ${fmtArgs(c.args)} — ${c.desc}`, "info");
    return;
  }
  const names = Object.keys(cmds).sort();
  append(`${names.length} commands:`, "info");
  for (const n of names) append(`  ${n.padEnd(18)} ${cmds[n].desc}`, "hint");
});

registerLocal("clear", [], "clear console output", () => {
  logs.innerHTML = "";
});

registerLocal("echo", [arg("text", "string")], "print text to console", (a) =>
  append(a.join(" ")),
);

registerLocal("print", [arg("text", "string")], "alias for echo", (a) =>
  append(a.join(" ")),
);

registerLocal(
  "bind",
  [arg("key", "string", true), arg("command", "string", true)],
  "bind a key to a command",
  (a) => {
    if (!a[0]) {
      const keys = Object.keys(keyBinds);
      if (!keys.length) return append("no binds", "hint");
      for (const k of keys) append(`  ${k} = ${keyBinds[k]}`, "hint");
      return;
    }
    if (!a[1]) {
      append(`${a[0]} = ${keyBinds[a[0]] || "(unbound)"}`, "info");
      return;
    }
    keyBinds[a[0]] = a.slice(1).join(" ");
    saveKeyBinds();
    append(`bound ${a[0]} -> ${keyBinds[a[0]]}`, "info");
  },
);

registerLocal("unbind", [arg("key", "string")], "remove a key bind", (a) => {
  if (!a[0]) return;
  delete keyBinds[a[0]];
  saveKeyBinds();
  append(`unbound ${a[0]}`, "info");
});

function resetAllKeyBinds(): void {
  const count = clearKeyBinds();
  append(
    count === 1 ? "reset 1 bind" : `reset ${count} binds`,
    count ? "info" : "hint",
  );
}

registerLocal("resetbinds", [], "reset all key binds", resetAllKeyBinds);
registerLocal("reset_binds", [], "alias for resetbinds", resetAllKeyBinds);

// ── Nav mesh visualizer ────────────────────────────────
// Draws the generated Source-style nav areas and area connections for the
// current map as 3D debug lines. `show_navmeshes off` clears it.
type NavOverlayState = {
  group: any | null;
  pathGroup: any | null;
  scene: any | null;
  mapKey: string | null;
};
const navOverlay: NavOverlayState = { group: null, pathGroup: null, scene: null, mapKey: null };
let navOverlayRebuildToken = 0;

type DebugOverlayRenderHookState = {
  renderHost: AnyRecord | null;
  originalRender: Function | null;
};
const debugOverlayRenderHook: DebugOverlayRenderHookState = { renderHost: null, originalRender: null };

function hasDebugOverlayRenderWork(): boolean {
  return !!(navOverlay.group || navOverlay.pathGroup || entBBoxOverlay.group);
}

function refreshDebugOverlaysAfterRender(): void {
  if (navOverlay.group || navOverlay.pathGroup) ensureNavOverlayOwnerCurrent();
  if (entBBoxOverlay.group) refreshEntBBoxOverlay();
}

function uninstallDebugOverlayRenderHookIfIdle(): void {
  if (hasDebugOverlayRenderWork()) return;
  if (debugOverlayRenderHook.renderHost && debugOverlayRenderHook.originalRender) {
    debugOverlayRenderHook.renderHost.render = debugOverlayRenderHook.originalRender;
  }
  debugOverlayRenderHook.renderHost = null;
  debugOverlayRenderHook.originalRender = null;
}

function installDebugOverlayRenderHook(): boolean {
  const host = RENDER as AnyRecord | null;
  if (!host || typeof host.render !== "function") return false;
  if (debugOverlayRenderHook.renderHost === host && debugOverlayRenderHook.originalRender) return true;
  if (debugOverlayRenderHook.renderHost && debugOverlayRenderHook.originalRender) {
    debugOverlayRenderHook.renderHost.render = debugOverlayRenderHook.originalRender;
  }
  const originalRender = host.render;
  host.render = function debugOverlayRenderHookFrame(this: AnyRecord, ...args: any[]) {
    const result = originalRender.apply(this, args);
    refreshDebugOverlaysAfterRender();
    uninstallDebugOverlayRenderHookIfIdle();
    return result;
  };
  debugOverlayRenderHook.renderHost = host;
  debugOverlayRenderHook.originalRender = originalRender;
  return true;
}

// Resolve the player whose reachable area / path we should visualize: the
// player being watched when spectating (CONTROLS.spect.target), otherwise the
// local player.
function getControlledPlayer(): Krunker.Player | null {
  const spectTarget = CONTROLS?.spect?.target as Krunker.Player | null | undefined;
  if (spectTarget && spectTarget.active && typeof spectTarget.x === "number") {
    return spectTarget;
  }
  return player || null;
}

// Remove a THREE group from the scene and dispose every geometry/material we
// created under it.
function disposeNavGroup(group: any | null): void {
  const scene = RENDER?.scene;
  if (group) {
    if (group.parent && typeof group.parent.remove === "function") {
      group.parent.remove(group);
    } else if (scene) {
      scene.remove(group);
    }
    group.traverse?.((obj: AnyRecord) => {
      obj.geometry?.dispose?.();
      const mat = obj.material;
      if (Array.isArray(mat)) mat.forEach((m: AnyRecord) => m.dispose?.());
      else mat?.dispose?.();
    });
  }
}

function clearNavOverlay(): void {
  navOverlayRebuildToken++;
  disposeNavGroup(navOverlay.pathGroup);
  disposeNavGroup(navOverlay.group);
  navOverlay.pathGroup = null;
  navOverlay.group = null;
  navOverlay.scene = null;
  navOverlay.mapKey = null;
  syncOfflineBotNavState();
  uninstallDebugOverlayRenderHookIfIdle();
}

function isNavMeshDebugEnabled(): boolean {
  return !!navOverlay.group;
}

function currentNavOverlayMapKey(): string {
  const game = Game as GameLike | null;
  const sandboxMapEntry = game?.isSandbox ? getActiveOfflineSandboxMapEntry() : null;
  if (sandboxMapEntry) {
    return `sandbox:${sandboxMapEntry.index}:${sandboxMapEntry.fileName || sandboxMapEntry.id}`;
  }
  const mapIndex = Number.isInteger(game?.mapIndex) ? game.mapIndex : "unknown";
  const mapData = getCurrentOriginalMapData() as AnyRecord | undefined;
  const mapName = mapData?.name || mapData?.id || mapData?.key || "map";
  return `map:${mapIndex}:${mapName}`;
}

function ensureNavOverlayOwnerCurrent(): boolean {
  if (!navOverlay.group) return false;
  const scene = RENDER?.scene || null;
  const mapKey = currentNavOverlayMapKey();
  if (!scene || navOverlay.scene !== scene || navOverlay.mapKey !== mapKey) {
    clearNavOverlay();
    return false;
  }
  return true;
}

function clearNavOverlayForMapSwitch(): boolean {
  const shouldRestore = !!(navOverlay.group || navOverlay.pathGroup);
  clearNavOverlay();
  return shouldRestore;
}

function scheduleNavOverlayRebuild(shouldRebuild: boolean): void {
  if (!shouldRebuild) return;
  const token = navOverlayRebuildToken;
  for (const delay of [0, 100, 300]) {
    window.setTimeout(() => {
      if (token !== navOverlayRebuildToken || navOverlay.group) return;
      if (!Game?.THREE || !RENDER?.scene || !getCurrentOriginalMapData()) return;
      buildGeneratedNavOverlay();
    }, delay);
  }
}

function buildGeneratedNavOverlay(): void {
  const THREE = Game?.THREE;
  const scene = RENDER?.scene;
  if (!THREE || !scene) {
    append("nav: renderer/scene not available (are you in a game?)", "error");
    return;
  }

  const mapData = getCurrentOriginalMapData();
  if (!mapData) {
    append("nav: no current map data captured yet", "error");
    return;
  }

  let generated: ReturnType<typeof buildGeneratedNavMesh>;
  try {
    generated = buildGeneratedNavMesh(mapData as AnyRecord);
  } catch (e) {
    append(`nav: buildGeneratedNavMesh failed: ${e instanceof Error ? e.message : e}`, "error");
    return;
  }

  const areaFillPts: number[] = [];
  const areaBorderPts: number[] = [];
  const crouchPts: number[] = [];
  const twoWayPts: number[] = [];
  const oneWayPts: number[] = [];
  const jumpPts: number[] = [];
  const dropPts: number[] = [];
  const obstaclePts: number[] = [];
  const sourceColors = {
    bg: 0x000000,
    normal: 0xff0000,
    crouch: 0x0000ff,
    jump: 0x00ff00,
    drop: 0xff0000,
    twoWay: 0x00ffff,
    oneWay: 0x0000ff,
    obstacle: 0xff0000,
  };

  const pushPoint = (dst: number[], point: NavOverlayPoint, lift = 0.8) => {
    dst.push(point.x, point.y + lift, point.z);
  };
  const pushLine = (dst: number[], a: NavOverlayPoint, b: NavOverlayPoint, lift = 1) => {
    pushPoint(dst, a, lift);
    pushPoint(dst, b, lift);
  };
  const pushTriangle = (dst: number[], a: NavOverlayPoint, b: NavOverlayPoint, c: NavOverlayPoint, lift = 0.8) => {
    pushPoint(dst, a, lift);
    pushPoint(dst, b, lift);
    pushPoint(dst, c, lift);
  };
  const centerOf = (area: (typeof generated.areas)[number]): NavOverlayPoint => ({
    x: (area.nw.x + area.ne.x + area.se.x + area.sw.x) / 4,
    y: (area.nw.y + area.ne.y + area.se.y + area.sw.y) / 4,
    z: (area.nw.z + area.ne.z + area.sw.z + area.se.z) / 4,
  });
  const insetPoint = (point: NavOverlayPoint, center: NavOverlayPoint, margin = 0.2): NavOverlayPoint => {
    const dx = center.x - point.x;
    const dz = center.z - point.z;
    const len = Math.hypot(dx, dz);
    if (len <= 0.0001) return point;
    return { x: point.x + (dx / len) * margin, y: point.y, z: point.z + (dz / len) * margin };
  };
  const pushArrow = (dst: number[], from: NavOverlayPoint, to: NavOverlayPoint, lift = 3, width = 5) => {
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const len = Math.hypot(dx, dz);
    if (len <= 0.0001) return;
    const ux = dx / len;
    const uz = dz / len;
    const px = -uz;
    const pz = ux;
    const head = Math.min(12, Math.max(5, len * 0.28));
    const base = { x: to.x - ux * head, y: to.y, z: to.z - uz * head };
    pushLine(dst, from, to, lift);
    pushLine(dst, to, { x: base.x + px * width, y: base.y, z: base.z + pz * width }, lift);
    pushLine(dst, to, { x: base.x - px * width, y: base.y, z: base.z - pz * width }, lift);
  };
  const hasReverseConnection = (fromId: number, toId: number): boolean => {
    const target = generated.areas[toId];
    if (!target) return false;
    return Object.values(target.connections).some((ids) => (ids || []).includes(fromId));
  };

  for (const area of generated.areas) {
    const center = centerOf(area);
    const nw = insetPoint(area.nw, center);
    const ne = insetPoint(area.ne, center);
    const se = insetPoint(area.se, center);
    const sw = insetPoint(area.sw, center);
    pushTriangle(areaFillPts, nw, se, ne);
    pushTriangle(areaFillPts, se, nw, sw);
    pushLine(areaBorderPts, nw, ne);
    pushLine(areaBorderPts, ne, se);
    pushLine(areaBorderPts, se, sw);
    pushLine(areaBorderPts, sw, nw);
    if (area.attrs & 1) pushLine(crouchPts, nw, se, 1.4);
    if (area.attrs & 4) {
      pushLine(obstaclePts, nw, ne, 1.8);
      pushLine(obstaclePts, ne, se, 1.8);
      pushLine(obstaclePts, se, sw, 1.8);
      pushLine(obstaclePts, sw, nw, 1.8);
    }
    const from = centerOf(area);
    for (const targets of Object.values(area.connections)) {
      for (const targetId of targets || []) {
        const target = generated.areas[targetId];
        if (!target) continue;
        const toCenter = centerOf(target);
        const dx = toCenter.x - from.x;
        const dz = toCenter.z - from.z;
        const len = Math.hypot(dx, dz);
        if (len <= 0.0001) continue;
        const start = { x: from.x + (dx / len) * 5, y: from.y, z: from.z + (dz / len) * 5 };
        const end = { x: from.x + (dx / len) * Math.min(22, len * 0.45), y: from.y + (toCenter.y - from.y) * 0.45, z: from.z + (dz / len) * Math.min(22, len * 0.45) };
        const link = generated.areaLinks?.[`${area.id}:${targetId}`];
        if (link?.kind === "jump") pushArrow(jumpPts, start, end, 3.2, 4);
        else if (link?.kind === "drop") pushArrow(dropPts, start, end, 3.2, 4);
        else if (link?.kind === "crouch") pushArrow(crouchPts, start, end, 3.2, 4);
        else if (hasReverseConnection(area.id, targetId)) {
          if (area.id < targetId) pushLine(twoWayPts, start, end, 3);
        } else {
          pushArrow(oneWayPts, start, end, 3, 4);
        }
      }
    }
  }

  const group = new THREE.Group();
  const mkMesh = (pts: number[], color: number, opacity: number) => {
    if (!pts.length) return null;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.renderOrder = 996;
    return mesh;
  };
  const mkLines = (pts: number[], color: number, onTop = false) => {
    if (!pts.length) return null;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
    const mat = new THREE.LineBasicMaterial({ color });
    if (onTop) {
      mat.depthTest = false;
      mat.depthWrite = false;
    }
    const lines = new THREE.LineSegments(geo, mat);
    if (onTop) lines.renderOrder = 999;
    return lines;
  };
  const fill = mkMesh(areaFillPts, sourceColors.bg, 0.16);
  const borders = mkLines(areaBorderPts, sourceColors.normal, true);
  const crouch = mkLines(crouchPts, sourceColors.crouch, true);
  const twoWay = mkLines(twoWayPts, sourceColors.twoWay, true);
  const oneWay = mkLines(oneWayPts, sourceColors.oneWay, true);
  const jump = mkLines(jumpPts, sourceColors.jump, true);
  const drop = mkLines(dropPts, sourceColors.drop, true);
  const obstacles = mkLines(obstaclePts, sourceColors.obstacle, true);
  for (const obj of [fill, borders, crouch, twoWay, oneWay, jump, drop, obstacles]) {
    if (obj) group.add(obj);
  }

  clearNavOverlay();
  scene.add(group);
  navOverlay.group = group;
  navOverlay.scene = scene;
  navOverlay.mapKey = currentNavOverlayMapKey();
  if (!installDebugOverlayRenderHook()) {
    clearNavOverlay();
    append("nav: renderer not available", "error");
    return;
  }
  syncOfflineBotNavState();
  append(
    `nav: generated source-style overlay ${generated.areas.length} areas, ${generated.nodes.length} nodes, ${generated.stats.obstacleLinks} obstacle links`,
    "info",
  );
}

registerLocal(
  "show_navmeshes",
  [arg("off", "string", true)],
  "visualize generated source-style nav meshes in 3D (off to clear)",
  (a) => {
    if (a[0] === "off" || a[0] === "0" || a[0] === "clear") {
      clearNavOverlay();
      append("nav: overlay cleared", "info");
      return;
    }
    buildGeneratedNavOverlay();
  },
);

// ── Entity bounding boxes ──────────────────────────────
// Source's ent_bbox toggles an orange movement-bounds overlay for a named
// entity/class, or for the entity under the crosshair when no argument is given.
type EntBBoxOverlayState = {
  group: any | null;
  scene: any | null;
  mode: EntBBoxMode;
  filter: string | null;
  boxes: AnyRecord[];
};
const entBBoxOverlay: EntBBoxOverlayState = { group: null, scene: null, mode: "look", filter: null, boxes: [] };
let entBBoxBoxCursor = 0;

function clearEntBBoxOverlay(message = true): void {
  disposeNavGroup(entBBoxOverlay.group);
  entBBoxOverlay.group = null;
  entBBoxOverlay.scene = null;
  entBBoxOverlay.mode = "look";
  entBBoxOverlay.filter = null;
  entBBoxOverlay.boxes = [];
  entBBoxBoxCursor = 0;
  uninstallDebugOverlayRenderHookIfIdle();
  if (message) append("ent_bbox: overlay cleared", "info");
}

function installEntBBoxRenderHook(): boolean {
  return installDebugOverlayRenderHook();
}

function entBBoxLabelForPlayer(candidate: AnyRecord): string {
  return String(candidate.alias || candidate.name || candidate.id || candidate.sid || "player");
}

function playerObject3D(candidate: AnyRecord): AnyRecord | null {
  return candidate.objInstances || candidate.object || candidate.mesh || null;
}

function collectEntBBoxPlayers(): EntBBoxTarget[] {
  const game = (sandboxGame || Game) as GameLike | null;
  const out: EntBBoxTarget[] = [];
  for (const candidate of game?.players?.list || []) {
    if (!candidate || candidate.active === false || candidate.spectating) continue;
    out.push({
      label: entBBoxLabelForPlayer(candidate),
      kind: "player",
      ref: candidate as AnyRecord,
      object3D: playerObject3D(candidate as AnyRecord),
    });
  }
  const local = getControlledPlayer() as AnyRecord | null;
  if (local && !out.some((entry) => entry.ref === local)) {
    out.push({ label: entBBoxLabelForPlayer(local), kind: "player", ref: local, object3D: playerObject3D(local) });
  }
  return out;
}

function objectLabel(object: AnyRecord, index: number): string {
  return String(object.name || object.id || object.uid || object.tag || object.constructor?.name || `object${index}`);
}

function collectEntBBoxObjects(): EntBBoxTarget[] {
  const game = (sandboxGame || Game) as GameLike | null;
  const manager = game?.map?.manager as AnyRecord | undefined;
  const rawLists = [manager?.objects, manager?.flags, manager?.pickups, manager?.spawnables, manager?.spinners, manager?.objectives];
  const seen = new Set<AnyRecord>();
  const out: EntBBoxTarget[] = [];
  let index = 0;
  for (const rawList of rawLists) {
    const list = Array.isArray(rawList) ? rawList : rawList && typeof rawList === "object" ? Object.values(rawList) : [];
    for (const object of list as AnyRecord[]) {
      if (!object || typeof object !== "object" || seen.has(object)) continue;
      const object3D = object.objInstances || object.object || object.mesh || object.meshRef || object;
      if (!object3D || typeof object3D !== "object") continue;
      seen.add(object);
      out.push({ label: objectLabel(object, index++), kind: "object", ref: object, object3D });
    }
  }
  return out;
}

function entBBoxFilterMatch(target: EntBBoxTarget, filter: string | null): boolean {
  if (!filter) return true;
  const needle = filter.toLowerCase();
  const ref = target.ref;
  return [target.label, target.kind, ref.name, ref.alias, ref.id, ref.uid, ref.sid, ref.tag, ref.constructor?.name]
    .filter((value) => value != null)
    .some((value) => String(value).toLowerCase().includes(needle));
}

function collectEntBBoxTargets(): EntBBoxTarget[] {
  if (entBBoxOverlay.mode === "look") {
    const looked = findLookEntBBoxTarget();
    return looked ? [looked] : [];
  }
  const players = entBBoxOverlay.mode === "objects" ? [] : collectEntBBoxPlayers();
  const objects = entBBoxOverlay.mode === "players" ? [] : collectEntBBoxObjects();
  return [...players, ...objects].filter((target) => entBBoxFilterMatch(target, entBBoxOverlay.filter));
}

function findLookEntBBoxTarget(): EntBBoxTarget | null {
  const THREE = Game?.THREE || window.THREE;
  if (!THREE || !RENDER?.camera || !RENDER?.scene) return null;
  const raycaster = new THREE.Raycaster();
  raycaster.far = 6000;
  raycaster.setFromCamera(new THREE.Vector2(0, 0), RENDER.camera);
  const local = getControlledPlayer() as AnyRecord | null;
  const hits = raycaster.intersectObject(RENDER.scene, true).filter((hit: AnyRecord) => {
    let object = hit.object;
    if (!object || object.visible === false) return false;
    while (object) {
      if (object === entBBoxOverlay.group) return false;
      if (local && object === playerObject3D(local)) return false;
      object = object.parent;
    }
    return true;
  });
  if (!hits.length) return null;
  const hitObject = hits[0].object as AnyRecord;
  for (const target of [...collectEntBBoxPlayers(), ...collectEntBBoxObjects()]) {
    let object = hitObject;
    while (object) {
      if (object === target.object3D) return target;
      object = object.parent;
    }
  }
  return { label: hitObject.name || hitObject.parent?.name || "look", kind: "object", ref: hitObject, object3D: hitObject };
}

function fillBoxPositions(positions: Float32Array, min: NavOverlayPoint, max: NavOverlayPoint): void {
  const pts = [
    min.x, min.y, min.z, max.x, min.y, min.z,
    max.x, min.y, min.z, max.x, min.y, max.z,
    max.x, min.y, max.z, min.x, min.y, max.z,
    min.x, min.y, max.z, min.x, min.y, min.z,
    min.x, max.y, min.z, max.x, max.y, min.z,
    max.x, max.y, min.z, max.x, max.y, max.z,
    max.x, max.y, max.z, min.x, max.y, max.z,
    min.x, max.y, max.z, min.x, max.y, min.z,
    min.x, min.y, min.z, min.x, max.y, min.z,
    max.x, min.y, min.z, max.x, max.y, min.z,
    max.x, min.y, max.z, max.x, max.y, max.z,
    min.x, min.y, max.z, min.x, max.y, max.z,
  ];
  for (let i = 0; i < pts.length; i++) positions[i] = pts[i];
}

// Pull a LineSegments from the overlay pool (or create one on first use) and
// update its geometry in place, so we avoid per-frame allocation/disposal.
function addLineBox(THREE: AnyRecord, group: AnyRecord, min: NavOverlayPoint, max: NavOverlayPoint, color = 0xff6400): void {
  let box = entBBoxOverlay.boxes[entBBoxBoxCursor];
  if (!box) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(new Float32Array(24 * 3), 3));
    const material = new THREE.LineBasicMaterial({ color, depthTest: false, depthWrite: false, transparent: true, opacity: 1 });
    box = new THREE.LineSegments(geometry, material);
    box.renderOrder = 1000;
    box.frustumCulled = false;
    entBBoxOverlay.boxes[entBBoxBoxCursor] = box;
  }
  const attribute = box.geometry.getAttribute("position");
  fillBoxPositions(attribute.array as Float32Array, min, max);
  attribute.needsUpdate = true;
  box.geometry.computeBoundingSphere?.();
  box.visible = true;
  if (box.parent !== group) group.add(box);
  entBBoxBoxCursor++;
}

function addPlayerMovementBBox(THREE: AnyRecord, group: AnyRecord, target: EntBBoxTarget): boolean {
  const ref = target.ref;
  if (![ref.x, ref.y, ref.z].every((value) => Number.isFinite(Number(value)))) return false;
  const radius = Math.max(2, Number(ref.scale) || 3.55);
  const height = Math.max(2, Number(ref.height) || Number(ref.maxHeight) || 11);
  addLineBox(
    THREE,
    group,
    { x: Number(ref.x) - radius, y: Number(ref.y), z: Number(ref.z) - radius },
    { x: Number(ref.x) + radius, y: Number(ref.y) + height, z: Number(ref.z) + radius },
  );
  return true;
}

function addObjectBBox(THREE: AnyRecord, group: AnyRecord, target: EntBBoxTarget): boolean {
  const object = target.object3D;
  if (!object) return false;
  const box = new THREE.Box3();
  try {
    box.setFromObject(object);
  } catch {
    return false;
  }
  if (box.isEmpty?.() || !Number.isFinite(box.min?.x) || !Number.isFinite(box.max?.x)) return false;
  addLineBox(THREE, group, box.min, box.max);
  return true;
}

function refreshEntBBoxOverlay(): void {
  const THREE = Game?.THREE || window.THREE;
  const scene = RENDER?.scene;
  if (!THREE || !scene) {
    clearEntBBoxOverlay(false);
    return;
  }
  if (entBBoxOverlay.scene && entBBoxOverlay.scene !== scene) {
    clearEntBBoxOverlay(false);
    return;
  }
  // Reuse a persistent group and pooled boxes across frames; only rebind to the
  // scene if the group was never created or got detached.
  let group = entBBoxOverlay.group;
  if (!group) {
    group = new THREE.Group();
    group.name = "ent_bbox_overlay";
    entBBoxOverlay.group = group;
  }
  if (group.parent !== scene) scene.add(group);
  group.visible = true;
  entBBoxBoxCursor = 0;
  let count = 0;
  for (const target of collectEntBBoxTargets()) {
    const ok = target.kind === "player"
      ? addPlayerMovementBBox(THREE, group, target) || addObjectBBox(THREE, group, target)
      : addObjectBBox(THREE, group, target);
    if (ok) count++;
  }
  // Hide any pooled boxes left over from a previous frame with more targets.
  for (let i = entBBoxBoxCursor; i < entBBoxOverlay.boxes.length; i++) {
    entBBoxOverlay.boxes[i].visible = false;
  }
  entBBoxOverlay.scene = scene;
  if (!count && entBBoxOverlay.mode !== "look") {
    // Keep the overlay active, but avoid silent confusion for filters that match nothing.
    group.visible = false;
  }
}

function startEntBBoxOverlay(mode: EntBBoxMode, filter: string | null): void {
  clearEntBBoxOverlay(false);
  entBBoxOverlay.mode = mode;
  entBBoxOverlay.filter = filter;
  if (!installEntBBoxRenderHook()) {
    append("ent_bbox: renderer not available", "error");
    return;
  }
  refreshEntBBoxOverlay();
  const target = mode === "filter" ? filter || "filter" : mode === "look" ? "look target" : mode;
  append(`ent_bbox: showing orange movement bounds for ${target}`, "info");
}

registerLocal(
  "ent_bbox",
  [arg("target", "string", true)],
  "display orange movement bounding boxes (target, all, players, objects, off)",
  (a, rest) => {
    const raw = String(rest || a[0] || "").trim();
    const normal = raw.toLowerCase();
    if (normal === "off" || normal === "0" || normal === "clear") {
      clearEntBBoxOverlay();
      return;
    }
    if (normal === "all") return startEntBBoxOverlay("all", null);
    if (normal === "players" || normal === "player") return startEntBBoxOverlay("players", null);
    if (normal === "objects" || normal === "object" || normal === "props") return startEntBBoxOverlay("objects", null);
    if (!raw) return startEntBBoxOverlay("look", null);
    return startEntBBoxOverlay("filter", raw);
  },
);

registerLocal(
  "alias",
  [arg("name", "string", true), arg("command", "string", true)],
  "define a command alias",
  (a) => {
    if (!a[0]) {
      const keys = Object.keys(aliases);
      if (!keys.length) return append("no aliases", "hint");
      for (const n of keys) append(`  ${n} = ${aliases[n]}`, "hint");
      return;
    }
    if (!a[1]) {
      append(`${a[0]} = ${aliases[a[0]] || "(undefined)"}`, "info");
      return;
    }
    const body = a.slice(1).join(" ");
    aliases[a[0]] = body;
    registerLocal(a[0], [], `alias for: ${body}`, () => exec(body));
    append(`alias ${a[0]} -> ${body}`, "info");
  },
);

registerLocal(
  "exec",
  [arg("commands", "string")],
  "run multiple commands separated by ;",
  (a) => {
    for (const part of a.join(" ").split(";")) {
      const t = part.trim();
      if (t) exec(t);
    }
  },
);

type ConnectTarget = {
  label: string;
  matchmakerURL: string;
  socialURL: string;
  apiURL: string;
  editorURL: string;
  enableHttps: boolean;
  allowHosts: string[];
};

function trimEndpoint(url: string): string {
  return url.replace(/\/+$/, "");
}

function isLocalConnectHost(hostname: string): boolean {
  return (
    hostname === "127.0.0.1" ||
    hostname === "localhost" ||
    hostname === "0.0.0.0" ||
    hostname.startsWith("192.168.")
  );
}

function endpointHost(url: string): string | null {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

function withUniqueHosts(hosts: Array<string | null>): string[] {
  const out: Record<string, boolean> = {};
  for (const host of hosts) {
    if (!host) continue;
    out[host.toLowerCase()] = true;
  }
  return Object.keys(out);
}

function resolveConnectTarget(input: string): ConnectTarget | null {
  const raw = input.trim();
  const target = raw || "local";
  const local =
    target.toLowerCase() === "local" || target.toLowerCase() === "default";
  if (local) {
    return {
      label: "local",
      matchmakerURL: location.origin + "/mm",
      socialURL: location.origin + "/social",
      apiURL: location.origin + "/api",
      editorURL: location.origin + "/editor",
      enableHttps: location.protocol === "https:",
      allowHosts: [],
    };
  }

  const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(target);
  let url: URL;
  try {
    url = new URL(hasScheme ? target : "https://" + target);
  } catch {
    return null;
  }

  const hostname = url.hostname.toLowerCase();
  if (!hostname) return null;

  if (isLocalConnectHost(hostname) || url.port) {
    const origin = trimEndpoint(url.origin);
    return {
      label: url.host,
      matchmakerURL: origin + "/mm",
      socialURL: origin + "/api",
      apiURL: origin + "/api",
      editorURL: origin + "/editor",
      enableHttps: url.protocol === "https:",
      allowHosts: [hostname],
    };
  }

  const root = hostname.replace(/^(matchmaker|social|api)\./, "");
  const matchmakerURL = "https://matchmaker." + root;
  const apiURL = "https://api." + root;
  const socialURL = root === "krunker.zip" ? apiURL : "https://social." + root;
  const editorURL = "https://editor." + root;

  return {
    label: root,
    matchmakerURL,
    socialURL,
    apiURL,
    editorURL,
    enableHttps: true,
    allowHosts: withUniqueHosts([
      root,
      endpointHost(matchmakerURL),
      endpointHost(socialURL),
      endpointHost(apiURL),
      endpointHost(editorURL),
    ]),
  };
}

function applyConnectTarget(target: ConnectTarget): void {
  setConnectTargetState(target);

  if (window.config) {
    window.config.matchmakerURL = target.matchmakerURL;
    window.config.socialURL = target.socialURL;
    window.config.apiURL = target.apiURL;
    window.config.editorURL = target.editorURL;
    window.config.enableHttps = target.enableHttps;
  }
  if (window.client) {
    window.client.matchmakerAddress = target.matchmakerURL;
  }
}

function clearPingRegionCache(): void {
  try {
    const cacheKey =
      typeof ClientManager?.PING_REGION_CACHE_KEY === "string"
        ? ClientManager.PING_REGION_CACHE_KEY
        : "pingRegion7";
    localStorage.removeItem(cacheKey);
  } catch {}
}

function formatConnectError(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  if (err && typeof err === "object") {
    const rec = err as Error & { response: { data: { error: Error } } };
    if (rec.message) return String(rec.message);
    if (rec.response?.data?.error) return String(rec.response.data.error);
  }
  return String(err || "unknown error");
}

function logConnectError(err: unknown): void {
  append("connect failed: " + formatConnectError(err), "error");
  console.error("[devConsole] connect failed", err);
}

async function seekThroughConnectTarget(): Promise<boolean> {
  if (
    !window.client ||
    typeof window.client.seek !== "function" ||
    typeof window.joinServer !== "function"
  ) {
    return false;
  }

  try {
    window.history.replaceState(document.title, document.title, "/");
  } catch {}

  const mmToken =
    typeof window.vWP_MMToken === "undefined" ? null : await window.vWP_MMToken;
  const seekResult = await window.client.seek({
    autoChangeGame: false,
    dataQuery: {
      v: window.config?.useLooseClient ? undefined : version,
    },
    validationToken: mmToken || undefined,
  });

  window.joinServer(
    seekResult.host,
    seekResult.port,
    seekResult.gameId,
    seekResult.clientId,
    null,
  );
  return true;
}

registerLocal(
  "connect",
  [arg("hostname", "string", true)],
  "seek and connect through a matchmaker hostname",
  async (a, rest) => {
    const input = (rest || a.join(" ")).trim();
    const target = resolveConnectTarget(input);
    if (!target) {
      append("connect <local|hostname>", "error");
      return;
    }

    applyConnectTarget(target);
    clearPingRegionCache();

    append(`Connecting through ${target.label}...`, "info");
    append(`  Matchmaker: ${target.matchmakerURL}`, "info");
    append(`  Social: ${target.socialURL}`, "info");
    append(`  API: ${target.apiURL}`, "info");

    try {
      const seeking = await seekThroughConnectTarget();
      if (seeking) return;
    } catch (err) {
      logConnectError(err);
      return;
    }

    if (window.io && typeof window.io.close === "function") {
      window.io.close();
    }
    append("endpoint updated; live seek is unavailable on this page", "warn");
  },
);

registerLocal(
  "disconnect",
  [],
  "disconnect and reset to default endpoints",
  () => {
    resetConnectState();
    clearPingRegionCache();
    if (window.io && typeof window.io.close === "function") {
      window.io.close();
    }
    location.href = "/";
  },
);

registerLocal("quit", [], "close the window", () => {
  window.close();
});

registerLocal("reconnect", [], "reload the current page", () => {
  location.reload();
});

function runOfflineSandbox(): void {
  const restoreNavOverlay = clearNavOverlayForMapSwitch();
  clearOfflineDeployables(Game as GameLike | null);
  const currentMapEntry =
    getActiveOfflineSandboxMapEntry() || offlineMapEntryForIndex(Game?.mapIndex);
  const currentMap = currentMapSandboxData();
  if (currentMap) {
    activeOfflineSandboxMapEntry = currentMapEntry;
    connectToSandbox(currentMap);
    window.setTimeout(() => clearOfflineDeployables(), 0);
    scheduleNavOverlayRebuild(restoreNavOverlay);
    return;
  }
  activeOfflineSandboxMapEntry = null;
  const blankMap = window.config && window.config.blankMap;
  const map = typeof blankMap === "string" ? blankMap : undefined;
  connectToSandbox(map);
  window.setTimeout(() => clearOfflineDeployables(), 0);
  scheduleNavOverlayRebuild(restoreNavOverlay);
}

function currentMapSandboxData(modeIndex?: number): string | undefined {
  try {
    const activeGame = getActiveOfflineGame() || Game;
    const mapIndex = activeGame?.mapIndex;
    const current =
      activeGame?.map?.maps &&
      mapIndex != null &&
      activeGame.map.maps[mapIndex as number];
    if (!current || typeof current !== "object") return undefined;

    const copy = { ...(current as AnyRecord) };
    const mapEntry = getActiveOfflineSandboxMapEntry() || offlineMapEntryForIndex(mapIndex);
    if (mapEntry) copy[OFFLINE_SANDBOX_MAP_INDEX_KEY] = mapEntry.index;
    if (Number.isInteger(modeIndex)) {
      const mapConfig = copy.config && typeof copy.config === "object" ? { ...(copy.config as AnyRecord) } : {};
      mapConfig.modes = [modeIndex];
      copy.config = mapConfig;
    }
    const currentPlayer = player || sandboxPlayer || null;
    if (currentPlayer) {
      copy.player = {
        rot: [Number(currentPlayer.xDire) || 0, Number(currentPlayer.yDire) || 0],
        pos: [Number(currentPlayer.x) || 0, Number(currentPlayer.y) || 0, Number(currentPlayer.z) || 0],
      };
    }
    return JSON.stringify(copy);
  } catch (err) {
    console.error("Failed to stage current map for sandbox", err);
    return undefined;
  }
}

function currentOfflineCustomInitPayload(mapIndex: number, modeIndex: number): { customData: unknown; modURL: unknown; customMapData: unknown } {
  const game = (sandboxGame || Game) as AnyRecord | null;
  if (!game) return { customData: null, modURL: 0, customMapData: null };
  if (!game.customMapData && !game.customData) return { customData: null, modURL: 0, customMapData: null };
  const activeEntry = getActiveOfflineSandboxMapEntry() || offlineMapEntryForLoadedMap(game as GameLike | null);
  if (activeEntry && activeEntry.index !== mapIndex) return { customData: null, modURL: 0, customMapData: null };

  const customMap = normalizeMapLike(game.customMapData || game.customData || game.map?.maps?.[mapIndex]);
  if (!customMap) return { customData: game.customData || null, modURL: game.modURL || 0, customMapData: game.customMapData || null };

  const copy = { ...customMap };
  if (Number.isInteger(modeIndex)) {
    const config = copy.config && typeof copy.config === "object" ? { ...(copy.config as AnyRecord) } : {};
    config.modes = [modeIndex];
    copy.config = config;
  }
  return {
    customData: game.customData || null,
    modURL: game.modURL || 0,
    customMapData: { data: JSON.stringify(copy) },
  };
}

registerLocal("offline", [], "switch to offline mode", () => {
  try {
    runOfflineSandbox();
    clearPingCounter();
  } catch (err) {
    clearPingCounter();
    append("offline failed: " + (err && err.message), "error");
  }
});

registerLocal("sandbox", [], "alias for offline mode", () => {
  try {
    runOfflineSandbox();
    clearPingCounter();
  } catch (err) {
    clearPingCounter();
    append("sandbox failed: " + (err && err.message), "error");
  }
});

registerLocal("ahk", [], "show current antihack key", () => {
  append(`IO AHK: ${io.ahNum} (base: ${ahk})`, "info");
});

registerLocal("version", [], "show current client version", () => {
  append(`Version: ${version}`, "info");
});

registerLocal("build", [], "alias for version", () => {
  append(`Version: ${version}`, "info");
});

registerLocal("verison", [], "alias for version", () => {
  append(`Version: ${version}`, "info");
});

registerLocal(
  "toggle",
  [arg("var", "string")],
  "toggle a server boolean command (god, noclip, ...)",
  (a) => {
    const v = a[0];
    if (!v) return append("toggle <var>", "error");
    sendServer(`/${v}`);
  },
);

registerLocal("say", [arg("text", "string")], "say something in chat", (a) => {
  const text = a.join(" ").trim();
  if (!text) return;
  sendServer("say " + text);
});

registerLocal("status", [], "show current server connection details", () => {
  printServerDetails();
});

registerLocal("serverinfo", [], "alias for status", () => {
  printServerDetails();
});

function getLocalTransformPlayer(): Krunker.Player | null {
  if (!player) {
    append("no local player", "error");
    return null;
  }
  return player;
}

function formatTransformValue(value: unknown): string {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(6) : "0.000000";
}

function printLocalPosition(): void {
  if (!player) return;
  append(
    `position: ${formatTransformValue(player.x)} ${formatTransformValue(player.y)} ${formatTransformValue(player.z)}`,
    "info",
  );
}

function printLocalRotation(): void {
  const player = getLocalTransformPlayer();
  if (!player) return;
  append(
    `rotation: yaw=${formatTransformValue(player.yaw)} pitch=${formatTransformValue(player.pitch)} xDire=${formatTransformValue(player.xDire)}`,
    "info",
  );
}

registerLocal("pos", [], "show local player position", () => {
  printLocalPosition();
});

registerLocal("position", [], "alias for pos", () => {
  printLocalPosition();
});

registerLocal("rot", [], "show local player rotation", () => {
  printLocalRotation();
});

registerLocal("rotation", [], "alias for rot", () => {
  printLocalRotation();
});

registerLocal("ping", [], "show current ping", () => {
  const pingSamples: unknown[] = Array.isArray(player && player.pings)
    ? player.pings
    : [];
  const latest = Number.isFinite(player && player.ping)
    ? Math.max(0, Math.round(player.ping))
    : null;
  const avg = pingSamples.length
    ? Math.round(
        pingSamples.reduce<number>(
          (sum: number, sample: unknown) => sum + (Number(sample) || 0),
          0,
        ) / pingSamples.length,
      )
    : null;

  const resolved = latest ?? avg ?? 0;
  append(`Ping: ${resolved}ms`, "info");
});

registerLocal(
  "thirdperson",
  [arg("on|off", "boolean", true)],
  "toggle client-side third person",
  runClientThirdPersonCommand,
);
registerLocal(
  "third",
  [arg("on|off", "boolean", true)],
  "alias for thirdperson",
  runClientThirdPersonCommand,
);
registerLocal(
  "tpmesh",
  [arg("on|off", "boolean", true)],
  "alias for thirdperson",
  runClientThirdPersonCommand,
);

// ── Server commands (mirror server/packetHandlers.ts cmds table) ──
//
// Keep arg signatures aligned with the server handlers so the hint
// row matches reality. The server has the final word on permissions.

const onOff = arg("on|off", "boolean", true);
const targetArg = arg("target", "string", true);

registerServer("god", [targetArg, onOff], "toggle god mode (self or target)");
registerServer("noclip", [targetArg, onOff], "toggle noclip (self or target)");
registerServer(
  "notarget",
  [targetArg, onOff],
  "dev: toggle whether bots ignore you as a target (self or target)",
);
registerServer(
  "smite",
  [targetArg, onOff],
  "toggle smite-on-hit (self or target)",
);
registerServer(
  "forcehide",
  [targetArg, onOff],
  "toggle forcehide (self or target)",
);
registerServer(
  "unlimitedammo",
  [targetArg, onOff],
  "toggle unlimited ammo (self or target)",
);
registerServer("c", [], "show connected players and menu count");
registerServer(
  "count",
  [],
  "alias for /c (show connected players and menu count)",
);
registerServer(
  "ping",
  [arg("name|id", "string", true)],
  "show your ping (devs can target players)",
);
registerServer("bhop", [arg("on|off", "boolean", true)], "toggle bhop");
registerServer("xp", [arg("amount", "number", true)], "add XP to your account");
registerServer("kr", [arg("amount", "number", true)], "add KR to your account");
registerServer(
  "kill",
  [arg("target", "string", true)],
  'kill yourself or a target (examples: /kill, /kill Guest_1, /kill "Wandering Ghost", /kill wanderingghost)',
);
registerServer("end", [], "set match timer to 3 seconds");
registerServer("time", [arg("MM:SS", "string")], "set the match timer");
registerServer("endtime", [arg("MM:SS", "string")], "set the end timer");
registerServer("endless", [], "toggle endless match timer");
registerServer("borders", [onOff], "toggle invisible barriers");
registerServer("forcecol", [onOff], "toggle force collide");
registerServer("server", [], "show current map/mode/player/tick info");
registerServer("cycle", [], "cycle to the next map");
registerServer(
  "map",
  [arg("name", "string", true)],
  "switch map (no arg = show current)",
);
registerLocal("maps", [], "list available maps", runMapsCommand);
registerServer("mode", [arg("alias", "string", true)], "switch game mode");
registerServer("modes", [], "list game modes");
registerServer("team", [arg("red|blue", "string")], "switch teams");
registerServer("teams", [], "list teams and players");
registerServer("class", [arg("name|id", "string", true)], "show or switch class");
registerServer("classes", [], "list classes and primary weapons");
registerServer("players", [], "list connected players (id, name, team)");
registerServer("ais", [], "list active AI entities (sid, name)");
registerServer("kick", [arg("name|id", "string", true)], "kick a player");
registerServer("ban", [arg("name|id", "string", true)], "ban a player account");
registerServer(
  "unban",
  [arg("accountId|name|id", "string", true)],
  "unban an account",
);
registerServer("bans", [], "list banned account ids");
registerServer("ip", [arg("name|id", "string", true)], "show a player's IP");
registerServer(
  "banip",
  [arg("name|id|ip", "string", true)],
  "ban an IP in this lobby",
);
registerServer("unbanip", [arg("ip", "string", true)], "unban an IP");
registerServer("ipbans", [], "list banned IPs");
registerServer(
  "score",
  [arg("amount", "number", true)],
  "add score (default +100)",
);
registerServer(
  "summon",
  [arg("name", "string", true)],
  "spawn a Soul Sanctum AI by name (no arg lists summonable names)",
);
registerServer("game", [arg("text", "string", true)], "send a game command");
registerServer(
  "bot",
  [arg("count", "number", true), arg("tfue|autoRespawn|key=value", "string", true)],
  "spawn player-targeting auto-respawn bots (tfue=off for full-strength bots)",
);
registerServer(
  "bot_add",
  [arg("count", "number", true), arg("tfue|autoRespawn|key=value", "string", true)],
  "alias for bot",
);
registerServer(
  "bot_type",
  [arg("nextbot|tfue", "string", true)],
  "show or switch bot manager type (switching removes old bots)",
);
registerServer(
  "bot_place",
  [arg("tfue|autoRespawn|key=value", "string", true)],
  "spawn a bot at your current position",
);
registerServer(
  "bot_kick",
  [arg("all|index|name", "string", true)],
  "remove bots by name/index or all bots",
);
registerServer(
  "bot_kill",
  [arg("all|index|name", "string", true)],
  "kill bots by name/index or all bots",
);
registerServer("bot_list", [], "list spawned bots");
registerServer(
  "bot_config",
  [arg("key=value", "string", true)],
  "show or update default bot options",
);
registerServer(
  "bot_mimic",
  [arg("on|off", "boolean", true)],
  "toggle bots mimicking their target inputs",
);
registerServer(
  "bot_stop",
  [arg("on|off", "boolean", true)],
  "toggle bot movement and attacking",
);
registerServer(
  "bot_difficulty",
  [arg("value", "number")],
  "set default bot difficulty",
);

// ── State ──────────────────────────────────────────────

const history: string[] = [];
let historyIdx = -1;
let draft = "";

const KEY_BINDS_KEY = "krk_dev_console_binds";
const keyBinds: Record<string, string> = Object.create(null);
const aliases: Record<string, string> = Object.create(null);
const offlineModeOverrides = new WeakMap<GameLike, number>();

function loadKeyBinds(): void {
  try {
    const raw = localStorage.getItem(KEY_BINDS_KEY);
    if (!raw) return;
    const stored = JSON.parse(raw);
    if (!stored || typeof stored !== "object" || Array.isArray(stored)) return;
    for (const [key, command] of Object.entries(stored)) {
      if (!key || typeof command !== "string" || !command.trim()) continue;
      keyBinds[key] = command;
    }
  } catch {}
}

function saveKeyBinds(): void {
  try {
    const keys = Object.keys(keyBinds).sort();
    if (!keys.length) {
      localStorage.removeItem(KEY_BINDS_KEY);
      return;
    }
    const stored: Record<string, string> = Object.create(null);
    for (const key of keys) stored[key] = keyBinds[key];
    localStorage.setItem(KEY_BINDS_KEY, JSON.stringify(stored));
  } catch {}
}

function clearKeyBinds(): number {
  const keys = Object.keys(keyBinds);
  for (const key of keys) delete keyBinds[key];
  try {
    localStorage.removeItem(KEY_BINDS_KEY);
  } catch {}
  return keys.length;
}

loadKeyBinds();

function getOfflineModeOverride(game: GameLike | null): number | null {
  if (!game) return null;
  const value = offlineModeOverrides.get(game);
  return Number.isInteger(value) && value >= 0 ? value : null;
}

function setOfflineModeOverride(
  game: GameLike | null,
  modeIndex: number | null,
): void {
  if (!game) return;
  if (!Number.isInteger(modeIndex) || modeIndex < 0) {
    offlineModeOverrides.delete(game);
    return;
  }
  offlineModeOverrides.set(game, modeIndex);
}

// ─── Execution ──────────────────────────────────────────

function getSocketStateLabel(socket: WebSocket | null | undefined): string {
  if (!socket) return "NO_SOCKET";
  switch (socket.readyState) {
    case WebSocket.CONNECTING:
      return "CONNECTING";
    case WebSocket.OPEN:
      return "OPEN";
    case WebSocket.CLOSING:
      return "CLOSING";
    case WebSocket.CLOSED:
      return "CLOSED";
    default:
      return String(socket.readyState);
  }
}

function getCurrentGameId(): string | null {
  try {
    return new URLSearchParams(
      window.location.search.replace("#", "%23").replace("+", "%2B"),
    ).get("game");
  } catch {
    return null;
  }
}

function getCurrentMapLabel(game: GameLike | null): string {
  const sandboxMapEntry = game?.isSandbox ? getActiveOfflineSandboxMapEntry() : null;
  if (sandboxMapEntry) {
    return `${sandboxMapEntry.name} (${sandboxMapEntry.index})`;
  }
  const mapIndex = game && game.mapIndex;
  const mapName = game?.map?.maps?.[mapIndex]?.name || game?.map?.name || null;
  if (mapName && Number.isInteger(mapIndex)) return `${mapName} (${mapIndex})`;
  if (mapName) return mapName;
  if (Number.isInteger(mapIndex)) return String(mapIndex);
  return "(unknown)";
}

function getCurrentModeLabel(game: GameLike | null): string {
  const modeIndex = game && game.modeIndex;
  const modeName = game?.mode?.alias || game?.mode?.name || null;
  if (modeName && Number.isInteger(modeIndex))
    return `${modeName} (${modeIndex})`;
  if (modeName) return modeName;
  if (Number.isInteger(modeIndex)) return String(modeIndex);
  return "(unknown)";
}

function printServerDetails(): void {
  const socket = io && io.socket;
  const humans = (Game?.players?.list || []).filter(
    (player: Krunker.Player) => player && !player.dummy && !player.spectating,
  );
  const activeHumans = humans.filter(
    (player: Krunker.Player) => player.active === true,
  );
  const status = io?.connected
    ? "connected"
    : socket
      ? "disconnected"
      : "no-socket";
  append(
    `Connection: ${status} | socket ${io?.socketId ?? "(none)"} | state ${getSocketStateLabel(socket)}`,
    "info",
  );
  append(`Endpoint: ${socket?.url || location.origin}`, "info");
  append(
    `Lobby: ${getCurrentGameId() || "(none)"} | map ${getCurrentMapLabel(Game)} | mode ${getCurrentModeLabel(Game)}`,
    "info",
  );
  append(
    `Players: ${activeHumans.length}/${humans.length} | host ${location.host}`,
    "info",
  );
}

function runMapsCommand(): void {
  if (!io.connected) {
    offlineServerCommands.maps([], "");
    return;
  }

  void fetch("/api/local-maps")
    .then((response) => {
      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
      return response.json();
    })
    .then((catalog) => {
      const builtIn = Array.isArray(catalog?.builtIn) ? catalog.builtIn : [];
      const custom = Array.isArray(catalog?.custom) ? catalog.custom : [];
      append(
        `Built-in (${builtIn.length}): ${builtIn.map((map: AnyRecord) => map.name).join(", ")}`,
        "info",
      );
      append(
        custom.length
          ? `Custom (${custom.length}): ${custom.map((map: AnyRecord) => map.name).join(", ")}`
          : "Custom (0): none",
        "info",
      );
    })
    .catch((err) => {
      append(`Failed to load maps: ${err && err.message ? err.message : err}`, "error");
    });
}

function parseToggleArg(value: unknown, currentValue: boolean): boolean {
  if (value == null || value === "") return !currentValue;
  const normalised = String(value).toLowerCase();
  if (normalised === "on" || normalised === "1" || normalised === "true") {
    return true;
  }
  if (normalised === "off" || normalised === "0" || normalised === "false") {
    return false;
  }
  return !currentValue;
}

function parseTimerMs(value: unknown): number | null {
  const raw = String(value || "").trim();
  if (!raw) return null;

  const parts = raw.split(":");
  if (parts.length === 2) {
    const minutes = Number(parts[0]);
    const seconds = Number(parts[1]);
    if (
      Number.isInteger(minutes) &&
      Number.isInteger(seconds) &&
      minutes >= 0 &&
      seconds >= 0 &&
      seconds < 60
    ) {
      return (minutes * 60 + seconds) * 1000;
    }
    return null;
  }

  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds >= 0 ? Math.round(seconds * 1000) : null;
}

function formatTimerMs(milliseconds: number): string {
  if (Game && typeof Game.getTime === "function") return Game.getTime(milliseconds);
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function sendOfflineTimerUpdate(milliseconds: number, index: number): void {
  const timerEvent = window.io?.events?.t;
  if (typeof timerEvent === "function") {
    timerEvent(formatTimerMs(milliseconds), index, index === 0 ? milliseconds : undefined);
  }
}

function localCommandUnavailable(message: string): void {
  append(message, "error");
}

function getOfflineCommandPlayer(): Krunker.Player | null {
  return sandboxPlayer || player || null;
}

function getActiveOfflineGame(): GameLike | null {
  return (sandboxGame || Game) as GameLike | null;
}

function enableSandboxDamage(game: GameLike | null = (sandboxGame || Game) as GameLike | null): void {
  if (!game?.isSandbox) return;
  game.config = game.config || {};
  game.config.frFire = false;
  game.config.t1Dmg = 1;
  game.config.t2Dmg = 1;
  game.config.t3Dmg = 1;
  game.config.t4Dmg = 1;
  if (Array.isArray(game.waitTimers) && game.waitTimers[0]) {
    game.waitTimers[0].canDMG = true;
    game.waitTimers[0].contTime = true;
  }
  installSandboxFriendlyFireGuard(game);
  installDevStatePersistenceGuard(game);
  for (const candidate of game.players?.list || []) {
    if (!candidate || candidate.spectating) continue;
    candidate.noPVP = false;
    candidate.forcePVP = false;
  }
  rebalanceSandboxTeams(game);
  if (game.bots?.config && game.bots.config.targetTeam !== false) {
    game.bots.config.targetTeam = false;
    game.bots.refreshConfig?.();
  }
}

function isSandboxFriendlyPlayerDamage(
  game: GameLike | null,
  victim: Krunker.Player | null | undefined,
  dealer: Krunker.Player | null | undefined,
  damage: unknown,
): boolean {
  if (!game?.isSandbox || !victim || !dealer || victim === dealer) return false;
  if ((Number(damage) || 0) <= 0) return false;
  if (!sandboxTeamCount(game)) return false;
  const victimTeam = sandboxTeamForPlayer(game, victim);
  const dealerTeam = sandboxTeamForPlayer(game, dealer);
  return !!victimTeam && victimTeam === dealerTeam;
}

function installSandboxFriendlyFireGuard(game: GameLike): void {
  const players = game.players as AnyRecord | undefined;
  if (!players) return;

  if (!players.__sandboxOrigChangeHealth && typeof players.changeHealth === "function") {
    players.__sandboxOrigChangeHealth = players.changeHealth;
    players.changeHealth = function guardedSandboxChangeHealth(this: AnyRecord, victim: Krunker.Player, dealer?: Krunker.Player | null, damage?: number, allowTeamDamage?: boolean, ...rest: any[]) {
      const activeGame = (sandboxGame || Game || game) as GameLike | null;
      if (isSandboxFriendlyPlayerDamage(activeGame, victim, dealer || null, damage)) return false;
      return players.__sandboxOrigChangeHealth.call(this, victim, dealer, damage, allowTeamDamage, ...rest);
    };
  }

  if (!players.__sandboxOrigKill && typeof players.kill === "function") {
    players.__sandboxOrigKill = players.kill;
    players.kill = function guardedSandboxKill(this: AnyRecord, victim: Krunker.Player, dealer?: Krunker.Player | null, ...rest: any[]) {
      const activeGame = (sandboxGame || Game || game) as GameLike | null;
      if (isSandboxFriendlyPlayerDamage(activeGame, victim, dealer || null, 1)) return false;
      return players.__sandboxOrigKill.call(this, victim, dealer, ...rest);
    };
  }
}

function sandboxTeamCount(game: GameLike | null): number {
  const mode = game?.mode;
  if (!mode) return 0;
  if (Array.isArray(mode.teamOptions) && mode.teamOptions.length) return mode.teamOptions.length;
  if (mode.multiTeam) return Math.max(2, Number(mode.multiTeam) || 2);
  if (mode.teams || mode.friendly || mode.clanWar) return 2;
  return 0;
}

function sandboxTeamForPlayer(game: GameLike, candidate: Krunker.Player): number {
  const teamCount = sandboxTeamCount(game);
  if (!teamCount || candidate.spectating) return 0;
  const current = Number(candidate.team) || 0;
  return current > 0 && current <= teamCount ? current : 1;
}

function setSandboxPlayerTeam(game: GameLike, candidate: Krunker.Player, team: number): void {
  if (!team || candidate.team === team) return;
  if (typeof game.updateTeam === "function") game.updateTeam(candidate, team);
  else candidate.team = team;
}

function rebalanceSandboxTeams(game: GameLike): void {
  if (!(game.mode?.teams && !game.mode?.teamOptions && !game.mode?.multiTeam)) {
    for (const candidate of game.players?.list || []) {
      if (!candidate || candidate.spectating) continue;
      setSandboxPlayerTeam(game, candidate, sandboxTeamForPlayer(game, candidate));
    }
    return;
  }
  const counts = new Map<number, number>([[1, 0], [2, 0]]);
  const players = (game.players?.list || []).filter((candidate: Krunker.Player) => candidate && !candidate.spectating);
  for (const candidate of players) {
    if (candidate.isBot) continue;
    setSandboxPlayerTeam(game, candidate, 1);
    counts.set(1, (counts.get(1) || 0) + 1);
  }
  for (const candidate of players) {
    if (!candidate.isBot) continue;
    const team = (counts.get(1) || 0) <= (counts.get(2) || 0) ? 1 : 2;
    setSandboxPlayerTeam(game, candidate, team);
    counts.set(team, (counts.get(team) || 0) + 1);
  }
}

function sandboxTeamName(game: GameLike | null, team: number): string {
  const index = (Number(team) || 0) - 1;
  if (index < 0) return "";
  return game?.mode?.teamOptions?.[index] || (window as AnyRecord).config?.teamNames?.[index] || `Team ${team}`;
}

function offlinePlayerRefs(): Krunker.Player[] {
  const socketId = (window as AnyRecord).io?.socketId;
  const sandboxPlayers = sandboxGame?.players as AnyRecord | undefined;
  const sandboxLocalPlayer =
    (socketId != null && typeof sandboxPlayers?.findById === "function" ? sandboxPlayers.findById(socketId) : null) ||
    (Array.isArray(sandboxPlayers?.list)
      ? sandboxPlayers.list.find((candidate: Krunker.Player) => candidate && (candidate.isYou || candidate.id === socketId))
      : null);
  const refs = [
    sandboxPlayer,
    sandboxLocalPlayer,
    player,
    (window as AnyRecord).tmpPlayer,
    Game && (Game as AnyRecord).tmpPlayer,
    sandboxGame && (sandboxGame as AnyRecord).tmpPlayer,
  ].filter(Boolean) as Krunker.Player[];
  return [...new Set(refs)];
}

function sandboxLocalPlayerRef(): Krunker.Player | null {
  const socketId = (window as AnyRecord).io?.socketId;
  const sandboxPlayers = sandboxGame?.players as AnyRecord | undefined;
  return (
    (socketId != null && typeof sandboxPlayers?.findById === "function" ? sandboxPlayers.findById(socketId) : null) ||
    (Array.isArray(sandboxPlayers?.list)
      ? sandboxPlayers.list.find((candidate: Krunker.Player) => candidate && (candidate.isYou || candidate.id === socketId))
      : null) ||
    sandboxPlayer ||
    null
  );
}

function visibleGamePlayerRefs(): Krunker.Player[] {
  const refs = [
    player,
    (window as AnyRecord).tmpPlayer,
    Game && (Game as AnyRecord).tmpPlayer,
  ].filter(Boolean) as Krunker.Player[];
  return [...new Set(refs)];
}

function applyVisibleGamePlayerTransform(snapshot: AnyRecord | null | undefined): void {
  const pos = Array.isArray(snapshot?.pos) ? snapshot.pos : null;
  if (!pos) return;
  const x = Number(pos[0]) || 0;
  const y = Number(pos[1]) || 0;
  const z = Number(pos[2]) || 0;
  const rot = Array.isArray(snapshot?.rot) ? snapshot.rot : [];
  const xDire = Number(rot[0]) || 0;
  const yDire = Number(rot[1]) || 0;

  for (const ref of visibleGamePlayerRefs()) {
    ref.x = x;
    ref.y = y;
    ref.z = z;
    ref.fakeX = x;
    ref.fakeY = y;
    ref.fakeZ = z;
    ref.xDire = xDire;
    ref.yDire = yDire;
    ref.forcePos = true;
    ref.inView = true;
  }
}

function syncVisibleGamePlayerTransformFromSandbox(): void {
  const source = sandboxLocalPlayerRef();
  if (!source) return;
  applyVisibleGamePlayerTransform({
    rot: [Number(source.xDire) || 0, Number(source.yDire) || 0],
    pos: [Number(source.x) || 0, Number(source.y) || 0, Number(source.z) || 0],
  });
}

function scheduleVisibleGamePlayerTransformSync(): void {
  for (const delay of [50, 150, 300]) {
    window.setTimeout(syncVisibleGamePlayerTransformFromSandbox, delay);
  }
}

function clearPingCounter(): void {
  const target = getOfflineCommandPlayer();
  if (target) {
    target.ping = 0;
    if (Array.isArray(target.pings)) target.pings.length = 0;
  }
  const menuPingText = document.getElementById("menuPingText");
  if (menuPingText) menuPingText.textContent = "0";
  const menuPingIcon = document.getElementById("menuPingIcon");
  if (menuPingIcon) menuPingIcon.style.color = "gray";
}

// Dev toggles (god, notarget, smite, forcehide, unlimitedammo, noclip) are set
// directly on the live local-player object. The engine rebuilds that object on
// every respawn (players.add -> player.init), which wipes the flags. We remember
// the desired state here and re-apply it whenever the local player is (re)spawned
// so dev states survive respawns. A null entry means "not managed".
type PersistentDevState = { value: boolean; onEnable: ((player: Krunker.Player) => void) | null };
const persistentDevStates = new Map<string, PersistentDevState>();

function setPersistentDevState(property: string, value: boolean, onEnable: ((player: Krunker.Player) => void) | null = null): void {
  persistentDevStates.set(property, { value, onEnable });
}

function applyPersistentDevStateTo(ref: Krunker.Player, runOnEnable = true): void {
  for (const [property, state] of persistentDevStates) {
    if (property === "noclip") continue; // noclip is driven through the engine event below
    ref[property] = state.value;
    if (runOnEnable && state.value && typeof state.onEnable === "function") state.onEnable(ref);
  }
}

function reapplyPersistentDevStates(): void {
  if (!persistentDevStates.size) return;
  for (const ref of offlinePlayerRefs()) applyPersistentDevStateTo(ref, false);
  const noclip = persistentDevStates.get("noclip");
  if (noclip) {
    for (const ref of offlinePlayerRefs()) ref.noclip = noclip.value;
  }
}

// Wrap the sandbox game's players.add so that any time the local player is added
// or rebuilt (the respawn path), the persisted dev states are re-applied.
function installDevStatePersistenceGuard(game: GameLike | null = (sandboxGame || Game) as GameLike | null): void {
  const players = game?.players as AnyRecord | undefined;
  if (!players || players.__devStatePersistInstalled || typeof players.add !== "function") return;
  players.__devStatePersistInstalled = true;
  const origAdd = players.add;
  players.add = function guardedDevStateAdd(this: AnyRecord, ...args: any[]) {
    const result = origAdd.apply(this, args);
    if (persistentDevStates.size && result) {
      const socketId = (window as AnyRecord).io?.socketId;
      if (result.isYou || (socketId != null && result.id === socketId)) applyPersistentDevStateTo(result as Krunker.Player);
    }
    return result;
  };
}

function offlineBoolCommand(
  property: string,
  label: string,
  args: string[],
  onEnable: ((player: Krunker.Player) => void) | null = null,
): boolean {
  const target = getOfflineCommandPlayer();
  if (!target) return true;
  const next = parseToggleArg(args[0], target[property] === true);
  setPersistentDevState(property, next, onEnable);
  installDevStatePersistenceGuard();
  for (const ref of offlinePlayerRefs()) {
    ref[property] = next;
    if (next && typeof onEnable === "function") onEnable(ref);
  }
  append(`${label} ${next ? "ON" : "OFF"}`, "info");
  return true;
}

function offlineNoclipCommand(args: string[]): boolean {
  const target = getOfflineCommandPlayer();
  if (!target) return true;
  const next = parseToggleArg(args[0], target.noclip === true);
  setPersistentDevState("noclip", next);
  installDevStatePersistenceGuard();
  const noclipEvent = window.io?.events?.noc;
  if ((sandboxGame || Game)?.isSandbox && typeof noclipEvent === "function") {
    noclipEvent(next);
  } else {
    target.noclip = next;
  }
  for (const ref of offlinePlayerRefs()) ref.noclip = next;
  append(`Noclip ${next ? "ON" : "OFF"}`, "info");
  return true;
}

function normaliseOfflineTarget(value: unknown): string {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function resolveOfflineKillTarget(query: unknown): OfflineKillResolve {
  const needle = normaliseOfflineTarget(query);
  if (!needle) return { kind: null, target: null, reason: "empty" };

  const playerMatches: Array<{ exact: boolean; target: Krunker.Player }> = [];
  const aiMatches: Array<{ exact: boolean; target: AIEntity }> = [];
  for (const candidate of Game.players?.list || []) {
    if (!candidate || candidate.dummy || candidate.active !== true) continue;
    const keys = [candidate.name, candidate.alias, candidate.id]
      .filter(Boolean)
      .map((value) => normaliseOfflineTarget(value));
    if (keys.includes(needle)) {
      playerMatches.push({ exact: true, target: candidate });
      continue;
    }
    if (keys.some((value) => value.startsWith(needle))) {
      playerMatches.push({ exact: false, target: candidate });
    }
  }
  for (const candidate of Game.AI?.ais || []) {
    if (!candidate || candidate.active !== true) continue;
    const keys = [candidate.name, candidate.sid]
      .filter(Boolean)
      .map((value) => normaliseOfflineTarget(value));
    if (keys.includes(needle)) {
      aiMatches.push({ exact: true, target: candidate });
      continue;
    }
    if (keys.some((value) => value.startsWith(needle))) {
      aiMatches.push({ exact: false, target: candidate });
    }
  }

  const exactPlayers = playerMatches.filter((match) => match.exact);
  const exactAis = aiMatches.filter((match) => match.exact);
  if (exactPlayers.length === 1 && exactAis.length === 0) {
    return { kind: "player", target: exactPlayers[0].target, reason: null };
  }
  if (exactAis.length === 1 && exactPlayers.length === 0) {
    return { kind: "ai", target: exactAis[0].target, reason: null };
  }
  if (exactPlayers.length + exactAis.length > 1) {
    return { kind: null, target: null, reason: "ambiguous" };
  }
  if (playerMatches.length === 1 && aiMatches.length === 0) {
    return { kind: "player", target: playerMatches[0].target, reason: null };
  }
  if (aiMatches.length === 1 && playerMatches.length === 0) {
    return { kind: "ai", target: aiMatches[0].target, reason: null };
  }
  if (playerMatches.length + aiMatches.length > 1) {
    return { kind: null, target: null, reason: "ambiguous" };
  }
  return { kind: null, target: null, reason: "not-found" };
}

function killOfflinePlayer(
  sourcePlayer: Krunker.Player | null,
  targetPlayer: Krunker.Player | null,
): boolean {
  if (!Game || !targetPlayer) return false;
  const hadGod = targetPlayer.godMode === true;
  try {
    if (hadGod) targetPlayer.godMode = false;
    if (Game.players && typeof Game.players.kill === "function") {
      Game.players.kill(targetPlayer, sourcePlayer, {});
      return true;
    }
    if (Game.players && typeof Game.players.changeHealth === "function") {
      Game.players.changeHealth(targetPlayer, sourcePlayer, 1e6);
      return true;
    }
    targetPlayer.active = false;
    targetPlayer.health = 0;
    return true;
  } finally {
    if (hadGod) targetPlayer.godMode = true;
  }
}

function killOfflineAI(
  sourcePlayer: Krunker.Player | null,
  targetAI: AIEntity | null,
): boolean {
  if (!targetAI || typeof targetAI.kill !== "function") return false;
  targetAI.kill(sourcePlayer || null);
  if (Game.AI && typeof Game.AI.sync === "function") {
    Game.AI.sync();
  }
  return true;
}

function runClientThirdPersonCommand(args: string[]): void {
  if (!Game) return;
  Game.config ||= {};
  Game.config.thirdPerson = parseToggleArg(args[0], Game.config.thirdPerson === true);

  const target = getOfflineCommandPlayer();
  if (!target || !Game.players || typeof Game.players.regenMeshes !== "function") {
    return;
  }
  Game.players.regenMeshes(target);
}

function getOfflineModeEntries(game: GameLike | null): ModeEntry[] {
  const modes = game?.map?.modes;
  if (!Array.isArray(modes)) return [];
  const entries: ModeEntry[] = [];
  for (let i = 0; i < modes.length; i++) {
    const mode = modes[i];
    if (!mode) continue;
    entries.push({
      index: i,
      mode,
      alias: (mode.alias || mode.id || mode.name || `mode${i}`).toLowerCase(),
      name: String(mode.name || mode.id || mode.alias || `Mode ${i}`),
    });
  }
  return entries;
}

function resolveOfflineModeIndex(
  game: GameLike | null,
  query: unknown,
): IndexResolve<{ entries: ModeEntry[] }> {
  const entries = getOfflineModeEntries(game);
  if (!entries.length) return { index: null, entries, reason: "no-modes" };
  const raw = String(query || "").trim();
  if (!raw) return { index: null, entries, reason: "empty" };

  const maybeIndex = Number(raw);
  if (Number.isInteger(maybeIndex)) {
    const hit = entries.find((entry) => entry.index === maybeIndex);
    if (hit) return { index: hit.index, entries, reason: null };
  }

  const needle = raw.toLowerCase();
  const exact = entries.filter((entry) => {
    const id = String(entry.mode?.id || "").toLowerCase();
    const name = String(entry.mode?.name || "").toLowerCase();
    return entry.alias === needle || id === needle || name === needle;
  });
  if (exact.length === 1) {
    return { index: exact[0].index, entries, reason: null };
  }
  if (exact.length > 1) {
    return { index: null, entries, reason: "ambiguous" };
  }

  const partial = entries.filter((entry) => {
    const id = String(entry.mode?.id || "").toLowerCase();
    const name = String(entry.mode?.name || "").toLowerCase();
    return (
      entry.alias.startsWith(needle) ||
      id.startsWith(needle) ||
      name.startsWith(needle)
    );
  });
  if (partial.length === 1) {
    return { index: partial[0].index, entries, reason: null };
  }
  if (partial.length > 1) {
    return { index: null, entries, reason: "ambiguous" };
  }

  return { index: null, entries, reason: "not-found" };
}

function normaliseClassQuery(value: unknown): string {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function getClassEntries(game: GameLike | null): ClassEntry[] {
  const classConfig = game?.classConfig || [];
  const weaponConfig = game?.weaponConfig || [];
  const allowed = Array.isArray(game?.config?.classes) ? game.config.classes : null;
  const entries: ClassEntry[] = [];
  for (let index = 0; index < classConfig.length; index++) {
    if (allowed && !allowed.includes(index)) continue;
    const cfg = classConfig[index];
    if (!cfg || cfg.hide) continue;
    const primaryIndex = Array.isArray(cfg.loadout) ? cfg.loadout[0] : null;
    const primary = primaryIndex == null ? null : weaponConfig[primaryIndex];
    entries.push({
      index,
      classConfig: cfg,
      name: String(cfg.name || `Class ${index}`),
      primaryName: String(primary?.name || `Weapon ${primaryIndex ?? "?"}`),
    });
  }
  return entries;
}

function resolveClassIndex(game: GameLike | null, query: unknown): IndexResolve<{ entries: ClassEntry[] }> {
  const entries = getClassEntries(game);
  if (!entries.length) return { index: null, entries, reason: "no-modes" };
  const raw = String(query || "").trim();
  if (!raw) return { index: null, entries, reason: "empty" };

  const maybeIndex = Number(raw);
  if (Number.isInteger(maybeIndex)) {
    const hit = entries.find((entry) => entry.index === maybeIndex);
    if (hit) return { index: hit.index, entries, reason: null };
  }

  const needle = normaliseClassQuery(raw);
  const exact = entries.filter((entry) => normaliseClassQuery(entry.name) === needle);
  if (exact.length === 1) return { index: exact[0].index, entries, reason: null };
  if (exact.length > 1) return { index: null, entries, reason: "ambiguous" };

  const partial = entries.filter((entry) => normaliseClassQuery(entry.name).startsWith(needle));
  if (partial.length === 1) return { index: partial[0].index, entries, reason: null };
  if (partial.length > 1) return { index: null, entries, reason: "ambiguous" };
  return { index: null, entries, reason: "not-found" };
}

function currentClassLabel(game: GameLike | null, currentPlayer: Krunker.Player | null = getOfflineCommandPlayer()): string {
  const classIndex = Number(currentPlayer?.classIndex ?? game?.classIndex ?? 0);
  const entry = getClassEntries(game).find((candidate) => candidate.index === classIndex);
  return entry ? `${entry.name} (${entry.index}) - ${entry.primaryName}` : `Class ${classIndex}`;
}

function applyOfflineClass(index: number): void {
  const target = getOfflineCommandPlayer();
  if (!target || !Game) throw new Error("spawn before switching classes");
  const classConfig = Game.classConfig?.[index];
  if (!classConfig) throw new Error(`class ${index} not found`);
  target.classIndex = index;
  target.secIndex = classConfig.secondary ? (Number.isFinite(Number(target.secIndex)) ? Number(target.secIndex) : 2) : null;
  target.updateItems(Game, true, true, undefined, true, true);
  if (target.active && Number.isFinite(target.maxHealth)) target.health = target.maxHealth;
  const updateCamMouse = (window as AnyRecord).updateCamMouse;
  if (typeof updateCamMouse === "function") updateCamMouse(index);
}

function getOfflineMapEntries(): OfflineMapEntry[] {
  const builtInMaps = builtInMapsFactory() as AnyRecord[];
  const catalogEntries = Array.isArray((mapCatalog as any).data)
    ? ((mapCatalog as any).data as Array<{ id: number; name: string; fileName?: string }>)
    : [];

  const builtIn = builtInMaps.map((map, index) => {
    const catalog = catalogEntries.find((entry) => entry.id === index);
    const name = String(catalog?.name || map?.name || `Map ${index}`);
    return {
      index,
      map,
      name,
      id: String(catalog?.id ?? index),
      fileName: String(map?.fileName || ""),
      custom: false,
    };
  });

  const custom = catalogEntries
    .filter((entry) => Number.isInteger(entry.id) && entry.id >= CUSTOM_MAP_ID_START)
    .map((entry) => {
      const fileName = String(entry.fileName || entry.name || "");
      return {
        index: entry.id,
        map: { name: entry.name, id: entry.id, fileName },
        name: String(entry.name || fileName || `Map ${entry.id}`),
        id: String(entry.id),
        fileName,
        custom: true,
      };
    })
    .filter((entry) => !!entry.fileName);

  return [...builtIn, ...custom];
}

function offlineMapEntryForIndex(index: unknown): OfflineMapEntry | null {
  const numeric = Number(index);
  if (!Number.isInteger(numeric)) return null;
  return getOfflineMapEntries().find((entry) => entry.index === numeric) || null;
}

function offlineMapEntryFromSandboxData(value: unknown): OfflineMapEntry | null {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      return offlineMapEntryFromSandboxData(JSON.parse(value));
    } catch {
      return null;
    }
  }
  if (typeof value !== "object") return null;
  const record = value as AnyRecord;
  const direct = offlineMapEntryForIndex(record[OFFLINE_SANDBOX_MAP_INDEX_KEY]);
  if (direct) return direct;
  if (record.data) return offlineMapEntryFromSandboxData(record.data);
  return null;
}

function getActiveOfflineSandboxMapEntry(): OfflineMapEntry | null {
  if (activeOfflineSandboxMapEntry) return activeOfflineSandboxMapEntry;
  const activeGame = (sandboxGame || Game) as AnyRecord | null;
  const recovered = offlineMapEntryFromSandboxData(activeGame?.customMapData)
    || offlineMapEntryFromSandboxData(activeGame?.customData)
    || offlineMapEntryFromSandboxData((Game as AnyRecord | null)?.customMapData)
    || offlineMapEntryFromSandboxData((Game as AnyRecord | null)?.customData)
    || offlineMapEntryForLoadedMap(activeGame as GameLike | null)
    || offlineMapEntryForLoadedMap(Game as GameLike | null);
  if (recovered) activeOfflineSandboxMapEntry = recovered;
  return recovered;
}

function offlineMapEntryForLoadedMap(game: GameLike | null): OfflineMapEntry | null {
  const current = currentLoadedMapRecord(game);
  if (!current) return null;
  const names = [current.name, current.id, current.key, current.fileName]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());
  if (!names.length) return null;
  return getOfflineMapEntries().find((entry) => {
    const candidates = [entry.name, entry.id, entry.fileName, entry.map?.name, entry.map?.id, entry.map?.key]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase());
    return candidates.some((candidate) => names.includes(candidate));
  }) || null;
}

function currentLoadedMapRecord(game: GameLike | null): AnyRecord | null {
  const record = game as AnyRecord | null;
  const custom = normalizeMapLike(record?.customMapData || record?.customData);
  if (custom) return custom;
  const mapIndex = Number(record?.mapIndex);
  if (Number.isInteger(mapIndex) && record?.map?.maps?.[mapIndex]) return record.map.maps[mapIndex] as AnyRecord;
  if (record?.map && typeof record.map === "object") return record.map as AnyRecord;
  return null;
}

function activeOfflineMapIndex(game: GameLike | null): number {
  const entry = getActiveOfflineSandboxMapEntry() || offlineMapEntryForLoadedMap(game) || offlineMapEntryForLoadedMap(Game as GameLike | null);
  if (entry) return entry.index;
  const value = Number((game as AnyRecord | null)?.mapIndex);
  if (Number.isInteger(value)) return value;
  const fallback = Number((Game as AnyRecord | null)?.mapIndex);
  return Number.isInteger(fallback) ? fallback : 0;
}

function resolveOfflineMapIndex(
  game: GameLike | null,
  query: unknown,
): IndexResolve<{ entries: OfflineMapEntry[] }> {
  const entries = getOfflineMapEntries();
  if (!entries.length) return { index: null, entries, reason: "no-maps" };
  const raw = String(query || "").trim();
  if (!raw) return { index: null, entries, reason: "empty" };

  const maybeIndex = Number(raw);
  if (Number.isInteger(maybeIndex)) {
    const hit = entries.find((entry) => entry.index === maybeIndex);
    if (hit) return { index: hit.index, entries, reason: null };
  }

  const needle = raw.toLowerCase();
  const matches: Array<{ exact: boolean; index: number }> = [];
  for (const entry of entries) {
    const names = [entry.name, entry.id, entry.fileName, entry.map?.name, entry.map?.id, entry.map?.key]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase());
    if (names.includes(needle)) {
      matches.push({ exact: true, index: entry.index });
      continue;
    }
    if (names.some((value) => value.startsWith(needle))) {
      matches.push({ exact: false, index: entry.index });
    }
  }

  const exact = matches.filter((match) => match.exact);
  if (exact.length === 1) return { index: exact[0].index, entries, reason: null };
  if (exact.length > 1) return { index: null, entries, reason: "ambiguous" };
  if (matches.length === 1) return { index: matches[0].index, entries, reason: null };
  if (matches.length > 1) return { index: null, entries, reason: "ambiguous" };
  return { index: null, entries, reason: "not-found" };
}

function offlineInitAt(mapIndex: number, modeIndex: number): void {
  const io = window.io;
  const initEvent = io && io.events && io.events.init;
  if (
    !Game ||
    (typeof Game.init !== "function" && typeof initEvent !== "function")
  ) {
    throw new Error("game is not ready");
  }
  const restoreNavOverlay = clearNavOverlayForMapSwitch();
  const removedBots = resetOfflineBotsForMapSwitch();
  const customInit = currentOfflineCustomInitPayload(mapIndex, modeIndex);
  clearOfflineDeployables(Game as GameLike | null);
  if (typeof initEvent === "function") {
    initEvent(
      mapIndex,
      modeIndex,
      null,
      null,
      null,
      Game.config || { maps: [mapIndex] },
      customInit.customData as any,
      customInit.modURL as any,
      customInit.customMapData as any,
      null,
      null,
    );
    enableSandboxDamage(Game);
    clearOfflineDeployables(Game as GameLike | null);
    scheduleNavOverlayRebuild(restoreNavOverlay);
    if (removedBots) append(`Removed ${removedBots} bot${removedBots === 1 ? "" : "s"} for map reset.`, "warn");
    return;
  }
  if (customInit.customMapData) {
    (Game as AnyRecord).customData = customInit.customData;
    (Game as AnyRecord).modURL = customInit.modURL;
    (Game as AnyRecord).customMapData = customInit.customMapData;
  }
  Game.init(mapIndex, modeIndex, null, false);
  enableSandboxDamage(Game);
  clearOfflineDeployables(Game as GameLike | null);
  scheduleNavOverlayRebuild(restoreNavOverlay);
  if (removedBots) append(`Removed ${removedBots} bot${removedBots === 1 ? "" : "s"} for map reset.`, "warn");
}

function firstSpawnPlayerSnapshot(mapData: AnyRecord): AnyRecord | null {
  const spawn = Array.isArray(mapData.spawns) ? mapData.spawns[0] : null;
  if (!Array.isArray(spawn)) return null;
  const x = Number(spawn[0]) || 0;
  const y = Number(spawn[1]) || 0;
  const z = Number(spawn[2]) || 0;
  const dir = Number(spawn[3]) || 0;
  return {
    rot: [-((dir || 0) + 1) * Math.PI / 2, 0],
    pos: [x, y, z],
  };
}

function applyOfflinePlayerSnapshot(snapshot: AnyRecord | null | undefined, initialize = true): void {
  const pos = Array.isArray(snapshot?.pos) ? snapshot.pos : null;
  if (!pos) return;
  const x = Number(pos[0]) || 0;
  const y = Number(pos[1]) || 0;
  const z = Number(pos[2]) || 0;
  const rot = Array.isArray(snapshot?.rot) ? snapshot.rot : [];
  const xDire = Number(rot[0]) || 0;
  const yDire = Number(rot[1]) || 0;
  const games = [...new Set([sandboxGame, Game].filter(Boolean))] as GameLike[];

  for (const ref of offlinePlayerRefs()) {
    ref.x = x;
    ref.y = y;
    ref.z = z;
    ref.fakeX = x;
    ref.fakeY = y;
    ref.fakeZ = z;
    ref.xDire = xDire;
    ref.yDire = yDire;
    ref.forcePos = true;
    ref.inView = true;
    if (initialize) ref.setInitPos?.();
    for (const game of games) {
      game.players?.resetMovVals?.(ref);
    }
    if (ref.objInstances) (ref.objInstances as AnyRecord).visible = true;
  }
  for (const game of games) game.players?.sync?.();
}

function applyOfflinePlayerSnapshotFromPayload(payload: string): void {
  try {
    applyOfflinePlayerSnapshot(JSON.parse(payload)?.player);
  } catch {}
}

function removeOfflineKrunkScripts(mapData: AnyRecord): void {
  const scripts = mapData.scripts;
  if (!scripts || typeof scripts !== "object") return;
  for (const key of Object.keys(scripts)) {
    const value = scripts[key];
    if (typeof value !== "string" || !looksLikeBase64Script(value)) continue;
    try {
      const decoded = atob(value);
      if (looksLikeKrunkScript(decoded)) delete scripts[key];
    } catch {}
  }
  if (!Object.keys(scripts).length) delete mapData.scripts;
}

function looksLikeBase64Script(value: string): boolean {
  return value.length > 16 && value.length % 4 === 0 && /^[A-Za-z0-9+/]+={0,2}$/.test(value);
}

function looksLikeKrunkScript(value: string): boolean {
  return /(^|\n)\s*(#|public\s+action|action\s+|obj\s+|num\s+|str\s+|bool\s+)/.test(value);
}

async function loadOfflineSandboxMapPayload(entry: OfflineMapEntry, modeIndex: number): Promise<string> {
  if (!entry.fileName) throw new Error(`Map '${entry.name}' has no fileName`);
  const response = await fetch(`/maps/${entry.fileName}.json`);
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
  const mapData = await response.json() as AnyRecord;
  mapData[OFFLINE_SANDBOX_MAP_INDEX_KEY] = entry.index;
  const config = (mapData.config && typeof mapData.config === "object")
    ? { ...(mapData.config as AnyRecord) }
    : {};
  config.modes = [modeIndex];
  mapData.config = config;
  removeOfflineKrunkScripts(mapData);
  const playerSnapshot = firstSpawnPlayerSnapshot(mapData);
  if (playerSnapshot) mapData.player = playerSnapshot;
  return JSON.stringify(mapData);
}

function switchOfflineSandboxMap(entry: OfflineMapEntry, modeIndex: number, verb: string): void {
  loadOfflineSandboxMapPayload(entry, modeIndex)
    .then((payload) => {
      const restoreNavOverlay = clearNavOverlayForMapSwitch();
      const removedBots = resetOfflineBotsForMapSwitch();
      activeOfflineSandboxMapEntry = entry;
      clearOfflineDeployables();
      connectToSandbox(payload);
      scheduleVisibleGamePlayerTransformSync();
      window.setTimeout(() => {
        applyOfflinePlayerSnapshotFromPayload(payload);
        clearOfflineDeployables();
        enableSandboxDamage();
      }, 0);
      window.setTimeout(() => {
        applyOfflinePlayerSnapshotFromPayload(payload);
        clearOfflineDeployables();
        enableSandboxDamage();
        resetOfflineModeTransientState();
      }, 250);
      clearPingCounter();
      scheduleNavOverlayRebuild(restoreNavOverlay);
      append(`${verb} ${entry.name} (${entry.index}) [mode ${modeIndex}].${removedBots ? ` Removed ${removedBots} bot${removedBots === 1 ? "" : "s"}.` : ""}`, "info");
    })
    .catch((err) => {
      append(`${verb} failed: ${err && err.message ? err.message : err}`, "error");
      console.error(err);
    });
}

function rebuildOfflineSandboxMode(modeIndex: number, modeName: string): void {
  const activeGame = getActiveOfflineGame() || Game;
  const mapIndex = activeOfflineMapIndex(activeGame);
  const mapEntry =
    getActiveOfflineSandboxMapEntry() || offlineMapEntryForIndex(mapIndex);
  const payloadPromise = mapEntry?.fileName
    ? loadOfflineSandboxMapPayload(mapEntry, modeIndex)
    : Promise.resolve(currentMapSandboxData(modeIndex));

  payloadPromise
    .then((payload) => {
      if (!payload) throw new Error("current map data unavailable");
      const restoreNavOverlay = clearNavOverlayForMapSwitch();
      const removedBots = resetOfflineBotsForMapSwitch();
      activeOfflineSandboxMapEntry = mapEntry || getActiveOfflineSandboxMapEntry();
      clearOfflineDeployables();
      connectToSandbox(payload);
      scheduleVisibleGamePlayerTransformSync();
      window.setTimeout(() => {
        clearOfflineDeployables();
        enableSandboxDamage();
      }, 0);
      window.setTimeout(() => {
        clearOfflineDeployables();
        enableSandboxDamage();
        resetOfflineModeTransientState();
      }, 250);
      clearPingCounter();
      scheduleNavOverlayRebuild(restoreNavOverlay);
      append(`Mode set to ${modeName} (${modeIndex}); rebuilt sandbox.${removedBots ? ` Removed ${removedBots} bot${removedBots === 1 ? "" : "s"}.` : ""}`, "info");
    })
    .catch((err) => {
      append("Mode switch failed: " + (err && err.message ? err.message : err), "error");
      console.error(err);
    });
}

function reenterOfflineSandboxPlayer(): void {
  const reenter = (window as any).devConsoleReenterSandbox;
  if (typeof reenter !== "function") return;
  reenter();
  enableSandboxDamage();
}

function clearOfflineDeployables(game: GameLike | null = (sandboxGame || Game) as GameLike | null): void {
  if (!game) return;
  if (typeof game.resetSpawnables === "function") game.resetSpawnables();
  else if (game.map?.manager) game.map.manager.spawnables = [];
}

function resetOfflineModeTransientState(game: GameLike | null = (sandboxGame || Game) as GameLike | null): void {
  if (!game?.isSandbox) return;
  clearOfflineDeployables(game);

  for (const candidate of game.players?.list || []) {
    if (!candidate) continue;
    candidate.dogTags = 0;
    candidate.lastPicked = {};
    candidate.pickups = 0;
  }
}

function weaponHasUsableAmmo(player: Krunker.Player, index: number): boolean {
  const weaponId = Array.isArray(player.loadout) ? player.loadout[index] : null;
  const weapon = weaponId == null ? null : Game?.weaponConfig?.[weaponId];
  if (!weapon || weapon.melee || weapon.build || weapon.grapRang || weapon.deployable != null) return true;
  return (Number(player.ammos?.[index]) || 0) > 0 || (Number(player.reserveAmmos?.[index]) || 0) > 0;
}

function maybeSwapEmptyBotWeapon(game: GameLike, player: Krunker.Player): void {
  if (!player?.active || !Array.isArray(player.loadout) || player.loadout.length <= 1) return;
  const current = Number(player.loadoutIndex) || 0;
  const ammo = Number(player.ammos?.[current]) || 0;
  const reserve = Number(player.reserveAmmos?.[current]) || 0;
  const reload = Number(player.reloads?.[current]) || 0;
  const reloadTimer = Number(player.reloadTimer) || 0;
  const cantShootTimer = Number(player.cantShootTimer) || 0;
  if (ammo > 0 || reserve > 0 || reload > 0 || reloadTimer > 0 || cantShootTimer > 0) return;

  const next = player.loadout.findIndex((_weaponId: unknown, index: number) => index !== current && weaponHasUsableAmmo(player, index));
  if (next < 0) return;
  if (typeof game.players?.swapWeapon === "function") game.players.swapWeapon(player, null, true, undefined, next, true);
  else player.loadoutIndex = next;
}

class OfflineNextBot {
  readonly socketId = `offline-nextbot-${Math.random().toString(36).slice(2)}`;
  readonly worker: Worker;
  player: Krunker.Player | null = null;
  config: BotAIConfig;
  name: string;
  private mapKey: string | null = null;
  private respawnTimer = 0;
  private lastActive = false;

  constructor(private manager: OfflineNextBotManager, overrides: AnyRecord = {}) {
    this.config = { ...DEFAULT_AI_CONFIG, ...(manager.config || {}), ...filterAiConfig(overrides) };
    this.name = String(overrides.name || randomOfflineBotName());
    this.worker = new Worker("/js/bot.js");
    this.worker.onmessage = (event: MessageEvent<{ type: string; frames?: BotAIFrameSnapshot[] }>) => {
      if (event.data?.type === "frames" && event.data.frames) this.enqueueFrames(event.data.frames);
    };
  }

  spawn(spawnPoint?: AnyRecord | null): void {
    const game = this.manager.game;
    this.removePlayerOnly();
    const requestedClass = Number((this.config as AnyRecord).class);
    const classIndex = Number.isInteger(requestedClass) && isOfflineBotClassAllowed(game, requestedClass) ? requestedClass : randomOfflineBotClass(game);
    const classConfig = game.classConfig?.[classIndex] || game.classConfig?.[0];
    const team = this.manager.nextBotTeam();
    const spawn = spawnPoint || this.manager.nextSpawnPoint(team);
    const cosmetics = randomOfflineBotCosmetics(game, classIndex, classConfig);
    this.player = game.players.add(this.socketId, null, 0, 0, 0, this.name, classIndex, cosmetics.skins, cosmetics.face, cosmetics.shoes, cosmetics.hat, cosmetics.head, cosmetics.body, cosmetics.back, cosmetics.waist, cosmetics.pet, cosmetics.wrist, cosmetics.melee, cosmetics.skinColor, cosmetics.hairColor, cosmetics.dye, -1, -1, classConfig?.secondary ? 2 : null, null, team, false, false, null, null, this.name, false, 1, [-1, -1], [], true);
    this.player.account = this.player.account || (createOfflineBotAccount(this.name, this.player.sid) as any);
    this.player.accid = Number(this.player.account.id) || 0;
    this.player.alias = this.name;
    this.player.isBot = true;
    this.player.bypassUser = true;
    this.player.fakeLevel = 30 + (this.player.sid % 70);
    this.player.fakePremium = this.player.sid % 5 === 0 ? 1 : 0;
    this.player.fakeFeatured = this.player.sid % 11 === 0;
    this.player.fakeClan = offlineBotClan(this.player.sid);
    this.player.forcePVP = false;
    this.player.noPVP = false;
    this.player.team = team || this.player.team;
    this.player.x = Number(spawn.x) || 0;
    this.player.y = Number(spawn.y) || 0;
    this.player.z = Number(spawn.z) || 0;
    this.player.fakeX = this.player.x;
    this.player.fakeY = this.player.y;
    this.player.fakeZ = this.player.z;
    this.player.xDire = -(((Number(spawn.dir) || 0) + 1) * Math.PI) / 2;
    this.player.spawned = true;
    this.player.active = true;
    this.lastActive = true;
    this.respawnTimer = 0;
    this.player.setInitPos?.();
    ensureOfflineLocalPlayerAccount(game);
    enableSandboxDamage(game);
    rebalanceSandboxTeams(game);
    this.postSpawn();
    game.players.sync?.();
    game.players.syncLeaders?.(true);
  }

  update(delta: number): void {
    enableSandboxDamage(this.manager.game);
    if (!this.player?.active) {
      if (this.config.autoRespawn === false) return;
      if (this.lastActive && this.player) {
        this.worker.postMessage({ type: "death", sid: this.player.sid });
        this.respawnTimer = this.respawnDelay();
      }
      this.lastActive = false;
      this.respawnTimer -= delta;
      if (this.player && this.respawnTimer <= 0) this.respawn();
      return;
    }

    this.lastActive = true;
    maybeSwapEmptyBotWeapon(this.manager.game, this.player);
    this.postMapIfChanged();
    this.worker.postMessage({
      type: "tick",
      delta,
      selfId: this.socketId,
      selfSid: this.player.sid,
      players: this.snapshotPlayers(),
      modeObjective: this.manager.modeObjectiveState(),
    });
  }

  private respawn(): void {
    if (!this.player) return;
    const game = this.manager.game;
    const spawn = this.manager.nextSpawnPoint(this.player.team || 0, this.player);
    this.player.x = Number(spawn.x) || 0;
    this.player.y = Number(spawn.y) || 0;
    this.player.z = Number(spawn.z) || 0;
    this.player.fakeX = this.player.x;
    this.player.fakeY = this.player.y;
    this.player.fakeZ = this.player.z;
    this.player.xDire = -(((Number(spawn.dir) || 0) + 1) * Math.PI) / 2;
    this.player.health = Number(this.player.maxHealth) || 100;
    this.player.active = true;
    this.player.spawned = true;
    this.player.reloadTimer = 0;
    this.player.cantShootTimer = 0;
    this.player.sentTo = [];
    this.player.forcePos = true;
    this.player.inView = true;
    this.player.setInitPos?.();
    this.player.setClass?.(game, this.player.classIndex, this.player.secIndex, true);
    game.players?.resetMovVals?.(this.player);
    game.players?.regenMeshes?.(this.player);
    if (this.player.objInstances) (this.player.objInstances as AnyRecord).visible = true;
    game.players?.swapWeapon?.(this.player, 0, true);
    this.respawnTimer = 0;
    this.lastActive = true;
    enableSandboxDamage(game);
    rebalanceSandboxTeams(game);
    this.postSpawn();
    game.players?.sync?.();
    game.players?.syncLeaders?.(true);
  }

  remove(): void {
    this.removePlayerOnly();
    this.worker.terminate();
  }

  removePlayerOnly(): void {
    if (!this.player) return;
    const player = this.player;
    const sid = player.sid;
    player.active = false;
    player.spawned = false;
    player.inputs = [];
    player.sentTo = [];
    if (this.manager.game.players?.remove) this.manager.game.players.remove(sid);
    this.player = null;
    this.lastActive = false;
    this.respawnTimer = 0;
  }

  configure(config: Partial<BotAIConfig>): void {
    this.config = { ...this.config, ...config };
    this.worker.postMessage({ type: "configure", config: this.config });
  }

  spreadPlayer(point: AnyRecord): void {
    if (!this.player) return;
    this.player.x = Number(point.x) || this.player.x;
    this.player.y = Number(point.y) || this.player.y;
    this.player.z = Number(point.z) || this.player.z;
  }

  private postSpawn(): void {
    if (!this.player) return;
    this.mapKey = this.manager.mapKey();
    this.worker.postMessage({
      type: "spawn",
      config: this.config,
      selfId: this.socketId,
      selfSid: this.player.sid,
      players: this.snapshotPlayers(),
      mapData: this.manager.mapData(),
      modeObjective: this.manager.modeObjectiveState(),
    });
  }

  private respawnDelay(): number {
    const configured = Number(this.config.respawnDelay);
    if (Number.isFinite(configured) && configured > 0) return configured;
    if (this.config.useSpawnDelay) return Number((window as AnyRecord).config?.deathDelay) || 2800;
    return 0;
  }

  private postMapIfChanged(): void {
    const key = this.manager.mapKey();
    if (key === this.mapKey) return;
    this.mapKey = key;
    this.worker.postMessage({ type: "map", mapData: this.manager.mapData() });
  }

  syncNavState(): void {
    this.worker.postMessage({ type: "map", mapData: this.manager.mapData() });
  }

  private snapshotPlayers(): AnyRecord[] {
    return (this.manager.game.players?.list || [])
      .filter((candidate: Krunker.Player) => candidate && !candidate.spectating)
      .map((candidate: Krunker.Player) => remoteForOfflinePlayer(candidate, candidate === this.player));
  }

  private enqueueFrames(frames: BotAIFrameSnapshot[]): void {
    const player = this.player;
    if (!player?.active) return;
    for (const frame of frames) {
      const input = [frame.frameId, Math.max(1, Math.round(frame.delta)), Math.round(frame.pitch * 1000), Math.round(frame.yaw * 1000), -1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      for (const key of Object.keys(frame.fields || {})) {
        const index = Number(key);
        if (Number.isInteger(index) && index >= 0 && index < input.length) input[index] = frame.fields[index];
      }
      player.inputs.push(input as Krunker.InputFrame);
    }
  }
}

class OfflineNextBotManager {
  readonly list: OfflineNextBot[] = [];
  readonly gameManager = null;
  config: Partial<BotAIConfig> = { ...DEFAULT_AI_CONFIG, targetTeam: false };
  private teamToggle = 0;
  private spawnCursor = 0;

  constructor(readonly game: GameLike) {}

  add(spawnPoint?: AnyRecord | null, shouldSpawn = true, _skipSync = false, overrides: AnyRecord = {}): OfflineNextBot {
    const bot = new OfflineNextBot(this, overrides);
    this.list.push(bot);
    if (shouldSpawn) bot.spawn(spawnPoint);
    return bot;
  }

  remove(bot: OfflineNextBot): void {
    const index = this.list.indexOf(bot);
    if (index < 0) return;
    bot.remove();
    this.list.splice(index, 1);
  }

  update(delta: number): void {
    for (const bot of [...this.list]) bot.update(delta);
    reapplyPersistentDevStates();
  }

  refresh(): void {
    for (const bot of this.list) bot.spawn();
    this.sync();
  }

  spreadAround(point: AnyRecord): void {
    for (const bot of this.list) bot.spreadPlayer(point);
  }

  reset(): void {
    for (const bot of [...this.list].reverse()) this.remove(bot);
  }

  spawnPersistent(): void {
    for (const bot of this.list) bot.spawn();
  }

  nextSpawnPoint(team: number, player?: Krunker.Player | null): AnyRecord {
    const spawns = Array.isArray(this.game.map?.spawns) ? this.game.map.spawns : [];
    const normalizedTeam = Number(team) || 0;
    const candidates = spawns.filter((spawn: AnyRecord) => {
      if (!spawn || !Number.isFinite(Number(spawn.x)) || !Number.isFinite(Number(spawn.z))) return false;
      const spawnTeam = Number(spawn.team) || 0;
      if (!normalizedTeam) return !spawn.teamOnly;
      if (spawn.teamOnly) return spawnTeam === normalizedTeam;
      return !spawnTeam || spawnTeam === normalizedTeam;
    });
    const usable = candidates.length ? candidates : spawns;
    if (!usable.length) {
      return this.game.getSpawnPoint?.(normalizedTeam, player || null) || { x: 0, y: 0, z: 0, dir: 0 };
    }

    const index = this.spawnCursor++;
    const spawn = usable[index % usable.length] as AnyRecord;
    const point = {
      x: Number(spawn.x) || 0,
      y: Number(spawn.y) || 0,
      z: Number(spawn.z) || 0,
      dir: Number(spawn.dir) || 0,
    };
    if (usable.length === 1) {
      const radius = 24;
      const angle = (index % 12) * (Math.PI * 2 / 12);
      point.x += Math.cos(angle) * radius;
      point.z += Math.sin(angle) * radius;
    }
    return point;
  }

  sync(): void {
    this.game.players?.sync?.();
  }

  refreshConfig(): void {
    for (const bot of this.list) bot.configure(this.config);
  }

  configure(config: Partial<BotAIConfig>): number {
    this.config = { ...this.config, ...filterAiConfig(config) };
    this.refreshConfig();
    return this.list.length;
  }

  setupLocal(): void {
    for (let index = 0; index < 5; index++) this.add(null, true, false, { targetTeam: false });
  }

  nextBotTeam(): number {
    const teamCount = sandboxTeamCount(this.game);
    if (!teamCount) return 0;
    if (this.game.mode?.teams && !this.game.mode?.teamOptions && !this.game.mode?.multiTeam) {
      let teamOne = 0;
      let teamTwo = 0;
      for (const player of this.game.players?.list || []) {
        if (!player || player.spectating) continue;
        if (Number(player.team) === 1) teamOne++;
        else if (Number(player.team) === 2) teamTwo++;
      }
      return teamOne <= teamTwo ? 1 : 2;
    }
    this.teamToggle = this.teamToggle >= teamCount ? 1 : this.teamToggle + 1;
    return this.teamToggle;
  }

  mapKey(): string {
    return `${this.game.mapIndex}:${this.game.customMapData ? "custom" : "map"}`;
  }

  mapData(): AnyRecord | null {
    if (!isNavMeshDebugEnabled()) return null;
    return normalizeMapLike(this.game.customMapData || this.game.customData || this.game.map?.maps?.[this.game.mapIndex]);
  }

  modeObjectiveState(): AnyRecord | null {
    const modeId = String(this.game.mode?.id || this.game.mode?.alias || "");
    if (!["point", "ctf", "tdf", "dom", "depo", "depoffa", "kc"].includes(modeId)) return null;
    const manager = this.game.map?.manager as AnyRecord | undefined;
    return {
      modeId,
      activeObjective: Number.isFinite(Number(this.game.activeObjective)) ? Number(this.game.activeObjective) : null,
      objectives: (manager?.objectives || []).map((objective: AnyRecord) => ({ uid: Number(objective.uid) || 0, x: Number(objective.x) || 0, y: Number(objective.y) || 0, z: Number(objective.z) || 0 })),
      flags: (manager?.flags || []).map((flag: AnyRecord) => ({ uid: Number(flag.uid) || 0, team: Number(flag.team) || 0, x: Number(flag.x) || 0, y: Number(flag.y) || 0, z: Number(flag.z) || 0, orgX: Number(flag.orgX) || Number(flag.x) || 0, orgY: Number(flag.orgY) || Number(flag.y) || 0, orgZ: Number(flag.orgZ) || Number(flag.z) || 0, carrier: flag.carrier ? Number(flag.carrier) : null, neutral: !!flag.neutral })),
      domZones: (manager?.domzones || []).map((zone: AnyRecord) => ({ uid: Number(zone.uid) || 0, x: Number(zone.x) || 0, y: Number(zone.y) || 0, z: Number(zone.z) || 0, capturedBy: zone.capturedBy ? Number(zone.capturedBy) : null })),
      spawnables: Object.values(manager?.spawnables || {}).map((spawnable: any) => ({ sid: Number(spawnable.sid) || 0, type: Number(spawnable.i) || 0, team: Number(spawnable.team) || 0, x: Number(spawnable.x) || 0, y: Number(spawnable.y) || 0, z: Number(spawnable.z) || 0, active: spawnable.active !== false })),
    };
  }
}

function syncOfflineBotNavState(): void {
  const game = (sandboxGame || Game) as OfflineBotGame | null;
  const managers = game?.__botManagers;
  if (!managers) return;
  for (const manager of Object.values(managers)) {
    for (const bot of manager?.list || []) {
      if (typeof bot?.syncNavState === "function") bot.syncNavState();
    }
  }
}

function createOfflineBotAccount(name: string, sid: number): AnyRecord {
  const level = 30 + (sid % 70);
  const clan = offlineBotClan(sid);
  return {
    id: 8_000_000 + sid,
    name,
    alias: name,
    level,
    levelProg: 0,
    score: Math.pow(level, 2),
    clan,
    premiumT: sid % 5 === 0 ? 1 : 0,
    featured: sid % 11 === 0 ? 1 : 0,
    hack: 0,
    stats: {},
    activeChallenges: [],
  };
}

function offlineBotClan(seed: number): string | number {
  return seed % 3 === 0 ? OFFLINE_BOT_CLANS[Math.abs(seed) % OFFLINE_BOT_CLANS.length] : 0;
}

function ensureOfflineLocalPlayerAccount(game: GameLike): void {
  const socketId = (window as AnyRecord).io?.socketId;
  const players = game.players as AnyRecord | undefined;
  const local =
    (socketId != null && typeof players?.findById === "function" ? players.findById(socketId) : null) ||
    (Array.isArray(players?.list) ? players.list.find((candidate: Krunker.Player) => candidate && (candidate.isYou || candidate.id === socketId)) : null) ||
    sandboxPlayer ||
    player;
  if (!local) return;
  local.forcePVP = false;
  local.noPVP = false;
  if (local.account) return;
  const name = local.name || local.alias || "Local User";
  local.account = createOfflineBotAccount(name, Number(local.sid) || 0) as any;
  local.accid = Number(local.account.id) || 0;
}

function randomOfflineBotName(): string {
  const adjectives = ["Ray", "Nova", "Astra", "Byte", "Echo", "Flux", "Kilo", "Orbit", "Vector", "Zenith"];
  const nouns = ["Runner", "Ranger", "Scout", "Vandal", "Striker", "Ghost", "Cipher", "Pilot", "Hunter", "Agent"];
  return `${adjectives[Math.floor(Math.random() * adjectives.length)]}${nouns[Math.floor(Math.random() * nouns.length)]}`;
}

const OFFLINE_BOT_BLOCKED_CLASS_NAMES = new Set(["rocketeer"]);
const OFFLINE_BOT_BLOCKED_WEAPON_NAMES = new Set(["rocket launcher"]);

function isOfflineBotClassAllowed(game: GameLike, classIndex: number): boolean {
  const classConfig = game.classConfig?.[classIndex];
  if (!classConfig) return false;
  const className = String(classConfig.name || "").trim().toLowerCase();
  if (OFFLINE_BOT_BLOCKED_CLASS_NAMES.has(className)) return false;
  const primaryIndex = Array.isArray(classConfig.loadout) ? classConfig.loadout[0] : null;
  const primaryName = primaryIndex == null ? "" : String(game.weaponConfig?.[primaryIndex]?.name || "").trim().toLowerCase();
  return !OFFLINE_BOT_BLOCKED_WEAPON_NAMES.has(primaryName);
}

function offlineBotClassChoices(game: GameLike): number[] {
  const classes = Array.isArray(game.config?.classes) && game.config.classes.length ? game.config.classes : [0, 2, 6, 13];
  const allowed = classes.map((value) => Number(value)).filter((value) => Number.isInteger(value) && isOfflineBotClassAllowed(game, value));
  return allowed.length ? allowed : [0];
}

function randomOfflineBotClass(game: GameLike): number {
  const classes = offlineBotClassChoices(game);
  return Number(classes[Math.floor(Math.random() * classes.length)]) || 0;
}

function randomSkinIndex(game: GameLike, predicate: (skin: AnyRecord) => boolean): number {
  const skins = (game.store?.skins || []).filter(predicate);
  if (!skins.length) return -1;
  return Number(skins[Math.floor(Math.random() * skins.length)].index) || -1;
}

function randomOfflineBotCosmetics(game: GameLike, classIndex: number, classConfig: AnyRecord): AnyRecord {
  const loadout = classConfig?.loadout || [0];
  return {
    skinColor: -1,
    hairColor: -1,
    skins: [
      randomSkinIndex(game, (skin) => !skin.type && skin.weapon === loadout[0] + 1 && (skin.classIndex == null || skin.classIndex === classIndex)),
      classConfig?.secondary ? randomSkinIndex(game, (skin) => !skin.type && skin.weapon === 3 && (skin.classIndex == null || skin.classIndex === classIndex)) : -1,
    ],
    hat: randomSkinIndex(game, (skin) => skin.type === 1),
    head: randomSkinIndex(game, (skin) => skin.type === 15),
    body: randomSkinIndex(game, (skin) => skin.type === 2),
    back: randomSkinIndex(game, (skin) => skin.type === 14),
    waist: randomSkinIndex(game, (skin) => skin.type === 6),
    face: randomSkinIndex(game, (skin) => skin.type === 5),
    shoes: randomSkinIndex(game, (skin) => skin.type === 8),
    pet: -1,
    wrist: randomSkinIndex(game, (skin) => skin.type === 11),
    melee: randomSkinIndex(game, (skin) => skin.type === 3),
    dye: randomSkinIndex(game, (skin) => skin.type === 4),
  };
}

function filterAiConfig(value: AnyRecord): Partial<BotAIConfig> {
  const out: Partial<BotAIConfig> = {};
  for (const key of [
    "difficulty",
    "accuracy",
    "targetDistance",
    "attackTarget",
    "followTarget",
    "watchTarget",
    "roam",
    "randomJump",
    "slideHop",
    "forceCrouch",
    "targetTeam",
    "autoRespawn",
    "useSpawnDelay",
    "respawnDelay",
  ] as const) {
    if (value[key] != null) (out as AnyRecord)[key] = value[key];
  }
  return out;
}

function normalizeMapLike(value: unknown): AnyRecord | null {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      return normalizeMapLike(JSON.parse(value));
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== "object") return null;
  const record = value as AnyRecord;
  if (Array.isArray(record.objects)) return record;
  if (record.map) return normalizeMapLike(record.map);
  if (record.data) return normalizeMapLike(record.data);
  return null;
}

function remoteForOfflinePlayer(candidate: Krunker.Player, isSelf: boolean): AnyRecord {
  const weapon = candidate.weapon as AnyRecord | undefined;
  const projectile = weapon?.projectile;
  const projectileRange = projectile && !projectile.disable ? Number(projectile.range) : 0;
  return {
    id: candidate.id,
    sid: candidate.sid,
    x: Number(candidate.x) || 0,
    y: Number(candidate.y) || 0,
    z: Number(candidate.z) || 0,
    xDire: Number(candidate.xDire) || 0,
    team: Number(candidate.team) || 0,
    health: Number(candidate.health) || 0,
    active: !!candidate.active,
    isPlayer: isSelf,
    onGround: candidate.onGround !== false,
    height: Number(candidate.height) || 11,
    adjustedHeight: Number((candidate as AnyRecord).adjustedHeight) || Number(candidate.height) || 11,
    crouchVal: Number(candidate.crouchVal) || 0,
    noTarget: (candidate as AnyRecord).noTarget === true,
    weapon: weapon ? {
      range: (projectileRange || Number(weapon.range) || 700) * (Number((candidate as AnyRecord).rangeMlt) || 1),
      melee: !!weapon.melee,
      nAuto: !!weapon.nAuto,
      burst: !!weapon.burst,
      canThrow: !!weapon.canThrow,
    } : undefined,
    hasFlag: !!(candidate as AnyRecord).flag,
    dogTags: Number((candidate as AnyRecord).dogTags) || 0,
  };
}

function removeAllOfflineBots(manager: any): number {
  const list = Array.isArray(manager?.list) ? [...manager.list] : [];
  for (const bot of list) {
    if (typeof manager.remove === "function") manager.remove(bot);
    else if (typeof bot?.remove === "function") bot.remove();
  }
  return list.length;
}

function resetOfflineBotsForMapSwitch(): number {
  let removed = 0;
  for (const { manager } of offlineBotManagers()) {
    removed += removeAllOfflineBots(manager);
  }
  return removed;
}

function installOfflineBotManagers(): OfflineBotGame | null {
  const game = (sandboxGame || Game) as OfflineBotGame | null;
  if (!game || !game.isSandbox) return null;
  if (!game.__botManagers) {
    const tfue = game.bots;
    const nextbot = new OfflineNextBotManager(game);
    game.__botManagers = { tfue, nextbot };
    game.__botType = "nextbot";
    (game as AnyRecord).bots = nextbot;
  }
  return game;
}

function getOfflineBotType(): BotType {
  return installOfflineBotManagers()?.__botType === "tfue" ? "tfue" : "nextbot";
}

function parseOfflineBotType(value: unknown): BotType | null {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "next" || raw === "nextbot" || raw === "nextbots") return "nextbot";
  if (raw === "tfue" || raw === "legacy" || raw === "old") return "tfue";
  return null;
}

function switchOfflineBotType(type: BotType): { previous: BotType; removed: number } | null {
  const game = installOfflineBotManagers();
  if (!game || !game.__botManagers) return null;
  const previous = getOfflineBotType();
  const removed = removeAllOfflineBots(game.bots);
  game.__botType = type;
  (game as AnyRecord).bots = game.__botManagers[type];
  return { previous, removed };
}

function getOfflineBotManager(): any | null {
  const game = installOfflineBotManagers();
  return game && game.isSandbox && game.bots ? game.bots : null;
}

function offlineBotManagers(): Array<{ type: BotType; manager: any }> {
  const game = installOfflineBotManagers();
  if (!game || !game.__botManagers) return [];
  const entries: Array<{ type: BotType; manager: any }> = [];
  const seen = new Set<any>();
  for (const type of ["nextbot", "tfue"] as BotType[]) {
    const manager = game.__botManagers[type];
    if (!manager || seen.has(manager)) continue;
    seen.add(manager);
    entries.push({ type, manager });
  }
  if (game.bots && !seen.has(game.bots)) entries.push({ type: getOfflineBotType(), manager: game.bots });
  return entries;
}

function offlineBotList(manager: any): any[] {
  return Array.isArray(manager?.list) ? manager.list : [];
}

function parseOfflineBotAssignments(args: string[]): { count: number; config: AnyRecord; errors: string[] } {
  let count = 1;
  const config: AnyRecord = {};
  const errors: string[] = [];

  for (const token of args) {
    const raw = String(token || "").trim();
    if (!raw) continue;
    if (/^\d+$/.test(raw)) {
      count = Math.max(1, Math.min(64, Number(raw) || 1));
      continue;
    }
    if (raw.toLowerCase() === "tfue") {
      config.dmgMlt = 0.01;
      continue;
    }
    if (raw.toLowerCase() === "autorespawn") {
      config.autoRespawn = true;
      continue;
    }
    if (raw.toLowerCase() === "noautorespawn") {
      config.autoRespawn = false;
      continue;
    }
    if (raw.toLowerCase() === "usespawndelay") {
      config.useSpawnDelay = true;
      continue;
    }
    if (!raw.includes("=")) {
      errors.push(`Unknown bot option '${raw}'`);
      continue;
    }
    const separator = raw.indexOf("=");
    const key = raw.slice(0, separator).trim();
    const value = raw.slice(separator + 1).trim();
    if (!key) {
      errors.push(`Invalid bot option '${raw}'`);
      continue;
    }
    config[key] = key === "difficulty" ? parseOfflineBotDifficulty(value) : parseOfflineBotValue(value);
  }

  return { count, config, errors };
}

function parseOfflineBotValue(value: string): unknown {
  const lower = value.toLowerCase();
  if (lower === "true" || lower === "on") return true;
  if (lower === "false" || lower === "off") return false;
  const numeric = Number(value);
  return Number.isFinite(numeric) && value.trim() !== "" ? numeric : value;
}

function parseOfflineBotDifficulty(value: string): number | null {
  const lower = value.toLowerCase();
  if (lower in BOT_DIFFICULTY_PRESETS) return BOT_DIFFICULTY_PRESETS[lower as BotDifficultyMode];
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

function offlineBotMatchesQuery(bot: any, query: string, position: number): boolean {
  const raw = String(query || "").trim();
  const needle = normaliseOfflineTarget(raw);
  if (!needle || needle === "all" || needle === "*") return true;
  const index = Number(raw);
  if (Number.isInteger(index) && (index === position || index === bot?.player?.sid)) return true;
  const keys = [bot?.name, bot?.socketId, bot?.player?.name, bot?.player?.alias, bot?.player?.id, bot?.player?.sid]
    .filter(Boolean)
    .map((value) => normaliseOfflineTarget(value));
  return keys.includes(needle) || keys.some((key) => key.startsWith(needle));
}

function matchingOfflineBots(query: string): Array<{ type: BotType; manager: any; bot: any }> {
  const matches: Array<{ type: BotType; manager: any; bot: any }> = [];
  for (const entry of offlineBotManagers()) {
    offlineBotList(entry.manager).forEach((bot, position) => {
      if (offlineBotMatchesQuery(bot, query, position)) matches.push({ ...entry, bot });
    });
  }
  return matches;
}

function configureOfflineBots(config: AnyRecord): number {
  let updated = 0;
  for (const { manager } of offlineBotManagers()) {
    manager.config = { ...(manager.config || {}), ...config };
    if (typeof manager.refreshConfig === "function") manager.refreshConfig();
    for (const bot of offlineBotList(manager)) {
      bot.config = { ...(bot.config || {}), ...config };
      if (typeof bot.configure === "function") bot.configure(config);
    }
    updated += offlineBotList(manager).length;
  }
  return updated;
}

function offlineBotConfigLabel(config: AnyRecord): string {
  const entries = Object.keys(config || {}).sort().map((key) => `${key}=${String(config[key])}`);
  return entries.length ? entries.join(" ") : "defaults";
}

const offlineServerCommands: Record<
  string,
  (args: string[], rest?: string) => boolean
> = {
  c() {
    const players = (Game?.players?.list || []).filter((p) => p && !p.dummy);
    const active = players.filter((p) => p.active === true && !p.spectating);
    append(`${players.length} connected, ${players.length - active.length} in menu`, "info");
    return true;
  },
  count(args, rest) {
    return offlineServerCommands.c(args, rest);
  },
  server() {
    printServerDetails();
    return true;
  },
  ping(args) {
    const query = args.join(" ").trim();
    const target = query ? resolveOfflineKillTarget(query).target : getOfflineCommandPlayer();
    if (!target || !("ping" in target)) {
      append(query ? `Player '${query}' not found.` : "Ping: 0ms", query ? "error" : "info");
      return true;
    }
    const samples = Array.isArray(target.pings) ? target.pings : [];
    const latest = Number.isFinite(target.ping) ? Math.round(target.ping) : null;
    const avg = samples.length
      ? Math.round(samples.reduce((sum, sample) => sum + (Number(sample) || 0), 0) / samples.length)
      : null;
    append(`${target.name || target.alias || "Player"} ping: ${latest ?? avg ?? 0}ms`, "info");
    return true;
  },
  cycle() {
    if (!Game) {
      append("Offline cycle unavailable: game is not ready.", "error");
      return true;
    }
    const maps = getOfflineMapEntries();
    if (!maps.length) {
      append("Offline cycle unavailable: no map list.", "error");
      return true;
    }

    const activeGame = getActiveOfflineGame() || Game;
    const currentIndex = activeOfflineMapIndex(activeGame);
    const currentPosition = Math.max(0, maps.findIndex((entry) => entry.index === currentIndex));
    const nextMapEntry = maps[(currentPosition + 1) % maps.length];
    const nextIndex = nextMapEntry.index;
    const modeEntries = getOfflineModeEntries(Game);
    const currentMode = Number.isInteger(Game.modeIndex) ? Game.modeIndex : 0;
    const modeOverride = getOfflineModeOverride(Game);
    const modeIndex =
      Number.isInteger(modeOverride) &&
      modeEntries.some((entry) => entry.index === modeOverride)
        ? modeOverride
        : currentMode;

    try {
      if (Game?.isSandbox) {
        switchOfflineSandboxMap(nextMapEntry, modeIndex, "Cycled to");
        return true;
      }
      offlineInitAt(nextIndex, modeIndex);
      reenterOfflineSandboxPlayer();
      resetOfflineModeTransientState();
      const modeName =
        Game?.map?.modes?.[modeIndex]?.name ||
        Game?.map?.modes?.[modeIndex]?.id ||
        `mode ${modeIndex}`;
      append(`Cycled to ${nextMapEntry.name} (${nextIndex}) [${modeName}].`, "info");
    } catch (err) {
      append("Cycle failed: " + (err && err.message), "error");
    }
    return true;
  },
  map(args) {
    const maps = getOfflineMapEntries();
    if (!maps.length) {
      append("No built-in map list available.", "error");
      return true;
    }

    const raw = args.join(" ").trim();
    if (!raw) {
      append(`Current map: ${getCurrentMapLabel(Game)}`, "info");
      return true;
    }

    const resolved = resolveOfflineMapIndex(Game, raw);
    if (!Number.isInteger(resolved.index)) {
      if (resolved.reason === "ambiguous") {
        append(
          `Map '${raw}' is ambiguous. Use /maps to list choices.`,
          "error",
        );
      } else {
        append(`Map '${raw}' not found. Use /maps to list choices.`, "error");
      }
      return true;
    }

    const mapIndex = resolved.index;
    const modeEntries = getOfflineModeEntries(Game);
    const currentMode = Number.isInteger(Game.modeIndex) ? Game.modeIndex : 0;
    const modeOverride = getOfflineModeOverride(Game);
    const modeIndex =
      Number.isInteger(modeOverride) &&
      modeEntries.some((entry) => entry.index === modeOverride)
        ? modeOverride
        : currentMode;

    try {
      const nextMap = maps.find((entry) => entry.index === mapIndex);
      if (Game?.isSandbox && nextMap) {
        switchOfflineSandboxMap(nextMap, modeIndex, "Switched map to");
        return true;
      }
      offlineInitAt(mapIndex, modeIndex);
      reenterOfflineSandboxPlayer();
      resetOfflineModeTransientState();
      const nextName = nextMap?.name || `Map ${mapIndex}`;
      append(`Switched map to ${nextName} (${mapIndex}).`, "info");
    } catch (err) {
      console.error(err);
    }
    return true;
  },
  mode(args) {
    if (!Game) {
      append("Offline mode unavailable: game is not ready.", "error");
      return true;
    }
    const activeGame = getActiveOfflineGame() || Game;
    const raw = args.join(" ").trim();
    if (!raw) {
      append(`Current mode: ${getCurrentModeLabel(activeGame)}`, "info");
      const modeOverride = getOfflineModeOverride(activeGame) ?? getOfflineModeOverride(Game);
      if (Number.isInteger(modeOverride)) {
        append(`Cycle mode override: ${modeOverride}`, "hint");
      }
      return true;
    }

    if (raw.toLowerCase() === "default" || raw.toLowerCase() === "reset") {
      setOfflineModeOverride(Game, null);
      if (activeGame !== Game) setOfflineModeOverride(activeGame, null);
      append("Cycle mode override cleared.", "info");
      return true;
    }

    const resolved = resolveOfflineModeIndex(activeGame, raw);
    if (!Number.isInteger(resolved.index)) {
      if (resolved.reason === "ambiguous") {
        append(
          `Mode '${raw}' is ambiguous. Use /modes to list choices.`,
          "error",
        );
      } else if (resolved.reason === "no-modes") {
        append("No mode list available for this map.", "error");
      } else {
        append(`Mode '${raw}' not found. Use /modes to list choices.`, "error");
      }
      return true;
    }

    const modeIndex = resolved.index;
    const mapIndex = activeOfflineMapIndex(activeGame);
    try {
      setOfflineModeOverride(Game, modeIndex);
      if (activeGame !== Game) setOfflineModeOverride(activeGame, modeIndex);
      const modeName =
        resolved.entries.find((entry) => entry.index === modeIndex)?.name ||
        activeGame?.map?.modes?.[modeIndex]?.name ||
        activeGame?.map?.modes?.[modeIndex]?.id ||
        `mode ${modeIndex}`;
      if (activeGame?.isSandbox) {
        rebuildOfflineSandboxMode(modeIndex, modeName);
        return true;
      }
      offlineInitAt(mapIndex, modeIndex);
      append(`Mode set to ${modeName} (${modeIndex}).`, "info");
    } catch (err) {
      append("Mode switch failed: " + (err && err.message), "error");
    }
    return true;
  },
  modes() {
    if (!Game) {
      append("No active game.", "error");
      return true;
    }
    const entries = getOfflineModeEntries(Game);
    if (!entries.length) {
      append("No modes available.", "info");
      return true;
    }
    append(`Modes (${entries.length}):`, "info");
    for (const entry of entries) {
      const id = entry.mode?.id ? ` id:${entry.mode.id}` : "";
      const marker = entry.index === Game.modeIndex ? " *" : "";
      append(`  ${entry.index}: ${entry.name}${id}${marker}`, "hint");
    }
    const modeOverride = getOfflineModeOverride(Game);
    if (Number.isInteger(modeOverride)) {
      append(`Cycle mode override: ${modeOverride}`, "info");
    }
    return true;
  },
  classes() {
    const classes = getClassEntries(Game);
    if (!classes.length) {
      append("No class list available.", "error");
      return true;
    }
    append(`Classes (${classes.length}):`, "info");
    for (const entry of classes) {
      const marker = entry.index === getOfflineCommandPlayer()?.classIndex ? " *" : "";
      append(`  ${entry.index}: ${entry.name} - ${entry.primaryName}${marker}`, "hint");
    }
    return true;
  },
  class(args) {
    if (!Game) {
      append("No active game.", "error");
      return true;
    }
    const raw = args.join(" ").trim();
    if (!raw) {
      append(`Current class: ${currentClassLabel(Game)}`, "info");
      return true;
    }
    const resolved = resolveClassIndex(Game, raw);
    if (!Number.isInteger(resolved.index)) {
      if (resolved.reason === "ambiguous") append(`Class '${raw}' is ambiguous. Use /classes to list choices.`, "error");
      else append(`Class '${raw}' not found. Use /classes to list choices.`, "error");
      return true;
    }
    try {
      applyOfflineClass(resolved.index);
      append(`Class set to ${currentClassLabel(Game)}.`, "info");
    } catch (err) {
      append(`Class switch failed: ${err && err.message ? err.message : err}`, "error");
    }
    return true;
  },
  maps() {
    const maps = getOfflineMapEntries();
    if (!maps.length) {
      append("No map list available.", "error");
      return true;
    }
    const builtIn = maps.filter((entry) => !entry.custom);
    const custom = maps.filter((entry) => entry.custom);
    append(`Built-in maps (${builtIn.length}):`, "info");
    append(`${builtIn.map((entry) => entry.name).join(", ")}`);
    append(`Custom maps (${custom.length}):`, "info");
    append(custom.length ? `${custom.map((entry) => entry.name).join(", ")}` : "none");
    return true;
  },
  god(args) {
    return offlineBoolCommand("godMode", "God mode", args, (player) => {
      if (Number.isFinite(player.maxHealth)) player.health = player.maxHealth;
    });
  },
  noclip(args) {
    return offlineNoclipCommand(args);
  },
  notarget(args) {
    // Dev-only: flag the local player so sandbox bots refuse to acquire them as
    // a threat. The flag rides along on the bot snapshot (remoteForOfflinePlayer
    // -> noTarget), and KrunkerVision.IsThreatAlive drops notarget players.
    return offlineBoolCommand("noTarget", "No target", args);
  },
  smite(args) {
    return offlineBoolCommand("smite", "Smite", args);
  },
  forcehide(args) {
    return offlineBoolCommand("forceHide", "Forcehide", args);
  },
  unlimitedammo(args) {
    return offlineBoolCommand(
      "unlimitedAmmo",
      "Unlimited ammo",
      args,
      (player) => {
        player.canThrow = true;
      },
    );
  },
  kill(args) {
    const source = getOfflineCommandPlayer();
    if (!source) return true;
    const query = args.join(" ").trim();
    if (!query) {
      killOfflinePlayer(source, source);
      append("You killed yourself.", "info");
      return true;
    }
    const resolved = resolveOfflineKillTarget(query);
    if (!resolved.target) {
      append(
        resolved.reason === "ambiguous"
          ? `Kill target '${query}' is ambiguous.`
          : `Kill target '${query}' not found.`,
        "error",
      );
      return true;
    }
    if (resolved.kind === "player") {
      killOfflinePlayer(source, resolved.target);
      append(
        `Killed player ${resolved.target.name || resolved.target.alias || resolved.target.id}.`,
        "info",
      );
      return true;
    }
    if (resolved.kind === "ai") {
      killOfflineAI(source, resolved.target);
      append(
        `Killed AI ${resolved.target.name || resolved.target.sid}.`,
        "info",
      );
      return true;
    }
    return true;
  },
  score(args) {
    const target = getOfflineCommandPlayer();
    if (!target || !Game?.players || typeof Game.players.score !== "function") {
      return true;
    }
    const amount = args.length ? Number(args[0]) : 100;
    if (!Number.isFinite(amount) || amount === 0) {
      append("Usage: score <amount> (default 100).", "error");
      return true;
    }
    Game.players.score(target, amount);
    append(
      `${amount > 0 ? "+" : ""}${amount} score (total ${target.score}).`,
      "info",
    );
    return true;
  },
  team(args) {
    const mode = Game?.mode;
    const target = getOfflineCommandPlayer();
    if (!target || !Game) return true;
    if (
      !mode ||
      !mode.teams ||
      mode.friendly ||
      mode.clanWar ||
      mode.convTeam
    ) {
      append("/team only works in team modes.", "error");
      return true;
    }
    const arg = (args[0] || "").toLowerCase();
    const next =
      arg === "red" || arg === "1"
        ? 1
        : arg === "blue" || arg === "2"
          ? 2
          : null;
    if (next == null) {
      append("Usage: /team red|blue", "error");
      return true;
    }
    if (target.team === next) {
      append(`Already on ${arg}.`, "info");
      return true;
    }
    target.team = next;
    killOfflinePlayer(target, target);
    append(`Switched to ${arg} team.`, "info");
    return true;
  },
  players() {
    const players = (Game?.players?.list || []).filter((p) => p && !p.dummy);
    if (!players.length) return append("No players.", "info"), true;
    append(`Players (${players.length}): ${players.map((p) => p.name || p.alias || `Guest_${p.sid}`).join(", ")}`, "info");
    return true;
  },
  ais() {
    const ais = (Game?.AI?.ais || []).filter((ai) => ai && ai.active !== false);
    if (!ais.length) return append("No AI.", "info"), true;
    append(`AI (${ais.length}): ${ais.map((ai) => ai.name || ai.sid || "(unnamed)").join(", ")}`, "info");
    return true;
  },
  bot(args) {
    const bots = getOfflineBotManager();
    if (!bots || typeof bots.add !== "function") {
      append("Sandbox bots unavailable. Run /sandbox first.", "error");
      return true;
    }
    const parsed = parseOfflineBotAssignments(args);
    if (parsed.errors.length) {
      append(parsed.errors[0], "error");
      return true;
    }
    for (let index = 0; index < parsed.count; index++) {
      bots.add(null, true, false, parsed.config);
    }
    append(`Spawned ${parsed.count} sandbox bot${parsed.count === 1 ? "" : "s"}.`, "info");
    return true;
  },
  bot_add(args, rest) {
    return offlineServerCommands.bot(args, rest);
  },
  bot_type(args) {
    const type = parseOfflineBotType(args[0]);
    if (!type) {
      append(`Bot type: ${getOfflineBotType()} (use /bot_type nextbot|tfue)`, "info");
      return true;
    }
    const result = switchOfflineBotType(type);
    if (!result) {
      append("Sandbox bot type unavailable. Run /sandbox first.", "error");
      return true;
    }
    append(`Bot type set to ${type}; removed ${result.removed} ${result.previous} bot${result.removed === 1 ? "" : "s"}.`, "info");
    return true;
  },
  bot_place(args) {
    const target = getOfflineCommandPlayer();
    const bots = getOfflineBotManager();
    if (!target || !bots || typeof bots.add !== "function") {
      append("Sandbox bot placement unavailable. Spawn first.", "error");
      return true;
    }
    const parsed = parseOfflineBotAssignments(args);
    if (parsed.errors.length) {
      append(parsed.errors[0], "error");
      return true;
    }
    bots.add({ x: target.x, y: target.y, z: target.z, dir: 0 }, true, false, parsed.config);
    append("Spawned sandbox bot at your position.", "info");
    return true;
  },
  bot_list() {
    const liveEntries = offlineBotManagers().flatMap(({ type, manager }) => offlineBotList(manager).map((bot, index) => ({ type, bot, index })));
    if (!liveEntries.length) return append("No bots.", "info"), true;
    append(`Sandbox bots (${liveEntries.length}): ${liveEntries.map(({ type, bot, index }) => `${type}:${index}:${bot.name || bot.player?.name || "Bot"}${bot.player ? `#${bot.player.sid}` : ""}`).join(", ")}`, "info");
    return true;
  },
  bot_kick(args, rest) {
    const query = (rest || args.join(" ")).trim() || "all";
    const matches = matchingOfflineBots(query);
    if (!matches.length) return append(`No bots matched '${query}'.`, "error"), true;
    for (const { manager, bot } of matches) {
      if (typeof manager?.remove === "function") manager.remove(bot);
      else if (typeof bot.remove === "function") bot.remove();
    }
    append(`Removed ${matches.length} bot${matches.length === 1 ? "" : "s"}.`, "info");
    return true;
  },
  bot_kill(args, rest) {
    const query = (rest || args.join(" ")).trim() || "all";
    const source = getOfflineCommandPlayer();
    const matches = matchingOfflineBots(query).map((match) => match.bot);
    if (!matches.length) return append(`No bots matched '${query}'.`, "error"), true;
    let killed = 0;
    for (const bot of matches) {
      if (bot?.player && bot.player.active && killOfflinePlayer(source, bot.player)) killed++;
    }
    append(killed ? `Killed ${killed} bot${killed === 1 ? "" : "s"}.` : "Matched bots are not spawned.", killed ? "info" : "error");
    return true;
  },
  bot_config(args) {
    const bots = getOfflineBotManager();
    if (!bots) return append("Sandbox bots unavailable. Run /sandbox first.", "error"), true;
    if (!args.length) {
      append(`Bot config: ${offlineBotConfigLabel(bots.config || {})}`, "info");
      return true;
    }
    const parsed = parseOfflineBotAssignments(args);
    if (parsed.errors.length) return append(parsed.errors[0], "error"), true;
    const updated = configureOfflineBots(parsed.config);
    append(`Bot config updated (${offlineBotConfigLabel(parsed.config)}); ${updated} live bot${updated === 1 ? "" : "s"} updated.`, "info");
    return true;
  },
  bot_mimic(args) {
    const enabled = parseToggleArg(args[0], false);
    const updated = configureOfflineBots({ mimicTarget: enabled });
    append(`Bot mimic ${enabled ? "ON" : "OFF"} (${updated} updated).`, "info");
    return true;
  },
  bot_stop(args) {
    const stopped = parseToggleArg(args[0], false);
    const cfg = stopped
      ? { attackTarget: false, followTarget: false, watchTarget: false, roam: false }
      : { attackTarget: true, followTarget: true, watchTarget: true, roam: true };
    const updated = configureOfflineBots(cfg);
    append(`${stopped ? "Stopped" : "Resumed"} ${updated} bot${updated === 1 ? "" : "s"}.`, "info");
    return true;
  },
  bot_difficulty(args) {
    if (!args[0]) {
      const bots = getOfflineBotManager();
      const current = bots?.config?.difficulty ?? DEFAULT_AI_CONFIG.difficulty;
      append(`Bot difficulty: ${String(current)}`, "info");
      return true;
    }
    const value = parseOfflineBotDifficulty(args[0] || "1");
    if (value == null) {
      append("Usage: bot_difficulty <easy|normal|hard|max|number>", "error");
      return true;
    }
    const updated = configureOfflineBots({ difficulty: value });
    append(`Bot difficulty set to ${String(value)} (${updated} updated).`, "info");
    return true;
  },
  kick(args) {
    const query = args.join(" ").trim();
    const resolved = resolveOfflineKillTarget(query);
    if (resolved.kind !== "player") return append(`Player '${query}' not found.`, "error"), true;
    resolved.target.kicked = true;
    resolved.target.active = false;
    append(`Kicked ${resolved.target.name || resolved.target.id}.`, "info");
    return true;
  },
  ban(args) {
    const query = args.join(" ").trim();
    const resolved = resolveOfflineKillTarget(query);
    if (resolved.kind !== "player") return append(`Player '${query}' not found.`, "error"), true;
    resolved.target.banned = true;
    resolved.target.active = false;
    append(`Banned ${resolved.target.name || resolved.target.id}.`, "info");
    return true;
  },
  unban() {
    append("Offline ban list is session-only; clear by reloading/offline restart.", "info");
    return true;
  },
  bans() {
    const banned = (Game?.players?.list || []).filter((p) => p && p.banned);
    append(banned.length ? `Banned: ${banned.map((p) => p.name || p.id).join(", ")}` : "Ban list is empty.", "info");
    return true;
  },
  ip(args) {
    append(args.length ? "Offline players do not have socket IPs." : "IP unavailable offline.", "info");
    return true;
  },
  banip() {
    append("IP bans are unavailable offline.", "info");
    return true;
  },
  unbanip() {
    append("IP bans are unavailable offline.", "info");
    return true;
  },
  ipbans() {
    append("IP ban list is empty.", "info");
    return true;
  },
  borders(args) {
    if (!Game) return true;
    Game.config ||= {};
    const enabled = parseToggleArg(args[0], Game.config.disableB === true ? false : true);
    Game.config.disableB = !enabled;
    append(`Borders ${enabled ? "ON" : "OFF"}`, "info");
    return true;
  },
  forcecol(args) {
    applyForceCollideFromServer(parseToggleArg(args[0], false));
    append("Force collide updated.", "info");
    return true;
  },
  summon(args) {
    const target = getOfflineCommandPlayer();
    const query = args.join(" ").trim();
    if (!Game || !target || typeof Game.spawnSoulSanctumAI !== "function") {
      append("Summon unavailable in this offline game.", "error");
      return true;
    }
    if (!query && typeof Game.listSoulSanctumAIs === "function") {
      append(`Summonable: ${Game.listSoulSanctumAIs().join(", ")}`, "info");
      return true;
    }
    const spawned = Game.spawnSoulSanctumAI(query, {
      origin: { x: target.x, y: target.y, z: target.z, yaw: target.yaw },
      distance: 48,
    });
    append(spawned ? `Summoned ${spawned.aiName || query}.` : `Failed to summon ${query}.`, spawned ? "info" : "error");
    return true;
  },
  game(_args, rest) {
    append(`Game command received: ${rest || ""}`, "info");
    return true;
  },
  teams() {
    const players = Game?.players?.list;
    const mode = Game?.mode;
    if (!players || !players.length) {
      append("No players.", "info");
      return true;
    }
    if (!mode || !mode.teams || mode.convTeam) {
      append("Not a team mode.", "info");
      return true;
    }
    const groups = new Map();
    for (const p of players) {
      if (!p || p.spectating) continue;
      const team = p.team || 0;
      if (!groups.has(team)) groups.set(team, []);
      groups.get(team).push(p.name || `Guest_${p.sid}`);
    }
    for (const team of [...groups.keys()].sort((left, right) => left - right)) {
      const label = sandboxTeamName(Game as GameLike | null, team) || `Team ${team}`;
      const names = groups.get(team);
      append(`${label} (${names.length}): ${names.join(", ")}`, "info");
    }
    return true;
  },
  end() {
    if (!Game) return true;
    Game.endTimer = 0;
    Game.gameTimer = 3e3;
    Game.gameTimerP = 3e3;
    Game.lastTimer = null;
    sendOfflineTimerUpdate(3e3, 0);
    append("Match timer set to 3 seconds.", "info");
    return true;
  },
  time(args) {
    if (!Game) return true;
    const milliseconds = parseTimerMs(args[0]);
    if (milliseconds == null) {
      append("Usage: time <MM:SS>", "error");
      return true;
    }
    Game.endTimer = 0;
    Game.gameTimer = milliseconds;
    Game.gameTimerP = milliseconds;
    Game.lastTimer = null;
    sendOfflineTimerUpdate(milliseconds, 0);
    append(`Game time set to ${formatTimerMs(milliseconds)}.`, "info");
    return true;
  },
  endtime(args) {
    if (!Game) return true;
    const milliseconds = parseTimerMs(args[0]);
    if (milliseconds == null) {
      append("Usage: endtime <MM:SS>", "error");
      return true;
    }
    Game.endTimer = milliseconds;
    Game.lastTimer = null;
    sendOfflineTimerUpdate(milliseconds, 1);
    append(`End timer set to ${formatTimerMs(milliseconds)}.`, "info");
    return true;
  },
  endless() {
    if (!Game) return true;
    const isEndless = Game.endTimer === "inf";
    if (isEndless) {
      const restored =
        Game.forceStaticMap === true
          ? 240000
          : Game.mode && Game.mode.gameTime == null
            ? (Game.config && Game.config.gameTime ? Game.config.gameTime : 4) *
              60000
            : Game.mode.gameTime;
      Game.endTimer = 0;
      Game.gameTimer = restored;
      Game.lastTimer = null;
      append("Match timer resumed.", "info");
      return true;
    }
    Game.endTimer = "inf";
    Game.lastTimer = null;
    append("Match timer paused.", "info");
    return true;
  },
};

function maybeRunOfflineServerCommand(line: string): boolean {
  if (io.connected) return false;
  const stripped = line.startsWith("/") ? line.slice(1) : line;
  const space = stripped.indexOf(" ");
  const name = (
    space === -1 ? stripped : stripped.slice(0, space)
  ).toLowerCase();
  const rest = space === -1 ? "" : stripped.slice(space + 1);
  const handler = offlineServerCommands[name];
  if (!handler) return false;
  try {
    handler(parseArgs(rest), rest);
  } catch (err) {
    console.error(err);
    // append("error: " + (err && err.message), "error");
  }
  return true;
}

function normalizeDevConsoleCommand(line: string): string {
  return line.trim().replace(/^\/+/, "");
}

function sendServer(line: string): void {
  if (maybeRunOfflineServerCommand(line)) {
    return;
  }
  if (!window.io || typeof window.io.send !== "function") {
    append("not connected to server", "error");
    return;
  }
  const command = normalizeDevConsoleCommand(line);
  if (!command) return;
  try {
    window.io.send("devC", command);
  } catch (err) {
    append("send failed: " + (err && err.message), "error");
  }
}

function parseArgs(rest: string): string[] {
  // Source-style: split on whitespace, respect double-quoted strings.
  const out: string[] = [];
  const re = /"([^"]*)"|(\S+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(rest)) !== null) {
    out.push(m[1] !== undefined ? m[1] : m[2]);
  }
  return out;
}

export function exec(line: string): void {
  line = line.trim();
  if (!line) return;
  enableLogsAutoScroll();
  append(`] ${line}`, "cmd"); // Allow leading `/` so users can paste literal chat-style commands.
  const stripped = line.startsWith("/") ? line.slice(1) : line;
  const space = stripped.indexOf(" ");
  const name = (
    space === -1 ? stripped : stripped.slice(0, space)
  ).toLowerCase();
  const rest = space === -1 ? "" : stripped.slice(space + 1);
  const args = parseArgs(rest);

  const c = cmds[name];
  if (!c) {
    // append(`unknown command: ${name}`, "error");
    return;
  }

  if (c.kind === "local") {
    try {
      c.run(args, rest);
    } catch (err) {
      console.error(err);
      // append("error: " + (err && err.message), "error");
    }
  } else {
    sendServer("/" + stripped);
  }
}

// ─── Autocomplete / hints ───────────────────────────────

function fmtArgs(args: CommandArg[]): string {
  return args
    .map((a: CommandArg) =>
      a.optional ? `${a.name}: ${a.type}?` : `${a.name}: ${a.type}`,
    )
    .join("  ");
}

function clearHints(): void {
  hints.innerHTML = "";
  hints.classList.remove("isOpen");
}

function addHintItem(
  label: string,
  meta: string | null,
  active: boolean,
  onPick: (() => void) | null = null,
): void {
  const item = document.createElement("div");
  item.className = "hintItem" + (active ? " isActive" : "");
  item.textContent = label;
  if (meta) {
    const span = document.createElement("span");
    span.className = "hintMeta";
    span.textContent = `  ${meta}`;
    item.append(span);
  }
  if (onPick) {
    item.addEventListener("mousedown", (e) => {
      e.preventDefault();
      onPick();
      input.focus();
    });
  }
  hints.append(item);
}

function updateHints(): void {
  const v = input.value;
  clearHints();
  if (!v) return;
  const space = v.indexOf(" ");
  const head = (space === -1 ? v : v.slice(0, space)).toLowerCase();
  const headStripped = head.startsWith("/") ? head.slice(1) : head;

  if (space === -1) {
    const matches = Object.keys(cmds)
      .filter((n) => n.startsWith(headStripped))
      .slice(0, 12);
    if (!matches.length) return;
    hints.classList.add("isOpen");
    const activeIndex = Math.max(0, matches.indexOf(headStripped));
    for (let i = 0; i < matches.length; i++) {
      const name = matches[i];
      addHintItem(name, null, i === activeIndex, () => {
        input.value = name + " ";
        updateHints();
      });
    }
    return;
  }

  const c = cmds[headStripped];
  if (c) {
    hints.classList.add("isOpen");
    addHintItem(headStripped, `${fmtArgs(c.args)}  —  ${c.desc}`, true);
  } else {
    hints.classList.add("isOpen");
    addHintItem("(unknown command)", "will not be sent", true);
  }
}

function tabComplete(): void {
  const v = input.value;
  const space = v.indexOf(" ");
  if (space !== -1) return; // only complete the command name
  const headStripped = v.startsWith("/") ? v.slice(1) : v;
  const matches = Object.keys(cmds).filter((n) => n.startsWith(headStripped));
  if (matches.length === 1) {
    input.value = matches[0] + " ";
  } else if (matches.length > 1) {
    let prefix = matches[0];
    for (const m of matches) {
      while (!m.startsWith(prefix)) prefix = prefix.slice(0, -1);
    }
    if (prefix.length > headStripped.length) input.value = prefix;
  }
  updateHints();
}

// ─── Input handling ─────────────────────────────────────

// History/Tab/Escape navigation must use keydown — those keys never
// fire `input` events because they don't insert characters. Listen on
// window capture so the game cannot swallow them at document capture
// before the focused console input ever sees the event.
function handleConsoleKeydown(e: KeyboardEvent): void {
  if (!visible) return;

  const activeEl = document.activeElement;
  const focusInConsole = activeEl === input || activeEl === logs;

  if (e.key === "Escape" && focusInConsole) {
    e.preventDefault();
    e.stopPropagation();
    toggleConsole();
    return;
  }

  if (activeEl !== input) return;

  if (e.key === "ArrowUp") {
    e.preventDefault();
    e.stopPropagation();
    if (!history.length) return;
    if (historyIdx === -1) {
      draft = input.value;
      historyIdx = history.length - 1;
    } else if (historyIdx > 0) {
      historyIdx--;
    }
    input.value = history[historyIdx];
    updateHints();
  } else if (e.key === "ArrowDown") {
    e.preventDefault();
    e.stopPropagation();
    if (historyIdx === -1) return;
    if (historyIdx < history.length - 1) {
      historyIdx++;
      input.value = history[historyIdx];
    } else {
      historyIdx = -1;
      input.value = draft;
    }
    updateHints();
  } else if (e.key === "Tab") {
    e.preventDefault();
    e.stopPropagation();
    tabComplete();
  }
}

window.addEventListener("keydown", handleConsoleKeydown, true);

// `input` event fires after the character is actually inserted, which
// means it survives any upstream capture-phase keydown listener that
// calls stopImmediatePropagation (the engine does this for several
// keys). We use it both to refresh hints and to catch the toggle key
// (backtick / configured bind) — strip the inserted char and toggle.
input.addEventListener("input", (e) => {
  const bindKey = (window.devConsoleKey || "Backquote").replace(/^Key/, "");
  const toggleChars = new Set(["`", "~"]);
  // Map common bind codes to their printable character so a custom bind
  // can also toggle via this path.
  const bindChar =
    { Backquote: "`", Minus: "-", Equal: "=" }[
      window.devConsoleKey || "Backquote"
    ] || (bindKey.length === 1 ? bindKey.toLowerCase() : null);
  if (bindChar) toggleChars.add(bindChar);

  const data = (e as InputEvent).data;
  if (data && toggleChars.has(data)) {
    // Remove the just-inserted toggle character from the input value.
    const pos = input.selectionStart ?? input.value.length;
    const before = input.value.slice(0, Math.max(0, pos - data.length));
    const after = input.value.slice(pos);
    input.value = before + after;
    try {
      input.setSelectionRange(before.length, before.length);
    } catch {}
    toggleConsole();
    return;
  }

  updateHints();
});

// Form submit handles Enter — survives upstream stopImmediatePropagation
// on keydown. Also covers any browser autocomplete confirmation paths.
inputRow.addEventListener("submit", (e) => {
  e.preventDefault();
  e.stopPropagation();
  const line = input.value;
  input.value = "";
  clearHints();
  if (line.trim()) {
    history.push(line);
    if (history.length > 200) history.shift();
  }
  historyIdx = -1;
  draft = "";
  exec(line);
});

// Also stop key events from bubbling to the document while the
// console has focus (so WASD doesn't move the player when typing).
for (const t of ["keydown", "keyup", "keypress"]) {
  devOverlay.addEventListener(t, (e) => e.stopPropagation(), true);
}

// Click anywhere on the overlay (outside the modal) to close.
// (Disabled — the overlay now has pointer-events: none so the game
// stays interactive; clicks only hit the modal/grip themselves.)

// ─── Drag the modal by its titlebar ────────────────────────

// Persist position/size across sessions.
const GEOM_KEY = "krk_dev_console_geom";
const MIN_CONSOLE_WIDTH = 280;
const MIN_CONSOLE_HEIGHT = 160;

function loadGeom(): ConsoleGeom | null {
  try {
    const raw = localStorage.getItem(GEOM_KEY);
    if (!raw) return null;
    const g = JSON.parse(raw);
    if (!g || typeof g !== "object") return null;
    return g as ConsoleGeom;
  } catch {
    return null;
  }
}
let saveGeomTimer: ReturnType<typeof setTimeout> | 0 = 0;
function saveGeom(): void {
  clearTimeout(saveGeomTimer);
  saveGeomTimer = setTimeout(() => {
    if (modal.dataset.posCommitted !== "1") return;
    try {
      localStorage.setItem(
        GEOM_KEY,
        JSON.stringify({
          left: parseInt(modal.style.left || "0", 10),
          top: parseInt(modal.style.top || "0", 10),
          width: modal.offsetWidth,
          height: modal.offsetHeight,
        }),
      );
    } catch {}
  }, 150);
}

function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

function clampGeom(): void {
  const viewportWidth = Math.max(0, window.innerWidth);
  const viewportHeight = Math.max(0, window.innerHeight);
  const minWidth = Math.min(MIN_CONSOLE_WIDTH, viewportWidth);
  const minHeight = Math.min(MIN_CONSOLE_HEIGHT, viewportHeight);
  const width = clampNumber(modal.offsetWidth, minWidth, viewportWidth);
  const height = clampNumber(modal.offsetHeight, minHeight, viewportHeight);

  modal.style.minWidth = minWidth + "px";
  modal.style.minHeight = minHeight + "px";
  modal.style.width = width + "px";
  modal.style.height = height + "px";

  const left = parseInt(modal.style.left || "0", 10);
  const top = parseInt(modal.style.top || "0", 10);
  const maxX = Math.max(0, viewportWidth - width);
  const maxY = Math.max(0, viewportHeight - height);
  modal.style.left = clampNumber(left, 0, maxX) + "px";
  modal.style.top = clampNumber(top, 0, maxY) + "px";
}

function commitInitialPos(): void {
  // Switch from CSS-centered (left: 50%; transform) to absolute pixels
  // anchored at the current visual position. Idempotent.
  if (modal.dataset.posCommitted === "1") return;
  const rect = modal.getBoundingClientRect();
  modal.style.transform = "none";
  modal.style.left = rect.left + "px";
  modal.style.top = rect.top + "px";
  modal.dataset.posCommitted = "1";
  clampGeom();
}

function withMeasuredConsole(run: () => void): void {
  const previousDisplay = devOverlay.style.display;
  const previousVisibility = devOverlay.style.visibility;

  if (getComputedStyle(devOverlay).display === "none") {
    devOverlay.style.display = "block";
    devOverlay.style.visibility = "hidden";
  }

  try {
    run();
  } finally {
    devOverlay.style.display = previousDisplay;
    devOverlay.style.visibility = previousVisibility;
  }
}

// Restore saved geometry on load (deferred so the modal has laid out).
function restoreGeom(): void {
  withMeasuredConsole(() => {
    const g = loadGeom();
    if (g) {
      if (Number.isFinite(g.width) && g.width >= 280) {
        modal.style.width = g.width + "px";
      }
      if (Number.isFinite(g.height) && g.height >= 160) {
        modal.style.height = g.height + "px";
      }
      if (Number.isFinite(g.left) && Number.isFinite(g.top)) {
        modal.style.transform = "none";
        modal.style.left = g.left + "px";
        modal.style.top = g.top + "px";
        modal.dataset.posCommitted = "1";
      }
    }
    commitInitialPos();
    clampGeom();
    saveGeom();
  });
}
if (document.body) restoreGeom();
else document.addEventListener("DOMContentLoaded", restoreGeom, { once: true });
window.addEventListener("resize", () => {
  if (modal.dataset.posCommitted !== "1") return;
  clampGeom();
  saveGeom();
});

{
  let dragging = false;
  let dx = 0;
  let dy = 0;
  header.addEventListener("mousedown", (e) => {
    if (e.button !== 0 || e.target === closeBtn) return;
    commitInitialPos();
    dragging = true;
    const rect = modal.getBoundingClientRect();
    dx = e.clientX - rect.left;
    dy = e.clientY - rect.top;
    e.preventDefault();
  });
  window.addEventListener("mousemove", (e) => {
    if (!dragging) return;
    modal.style.left = e.clientX - dx + "px";
    modal.style.top = e.clientY - dy + "px";
    clampGeom();
  });
  window.addEventListener("mouseup", () => {
    if (dragging) saveGeom();
    dragging = false;
  });
}

// ─── Resize via bottom-right grip ─────────────────────────
{
  let resizing = false;
  let startX = 0;
  let startY = 0;
  let startW = 0;
  let startH = 0;
  grip.addEventListener("mousedown", (e) => {
    if (e.button !== 0) return;
    commitInitialPos();
    resizing = true;
    startX = e.clientX;
    startY = e.clientY;
    startW = modal.offsetWidth;
    startH = modal.offsetHeight;
    e.preventDefault();
  });
  window.addEventListener("mousemove", (e) => {
    if (!resizing) return;
    const left = parseInt(modal.style.left || "0", 10);
    const top = parseInt(modal.style.top || "0", 10);
    const maxWidth = Math.max(0, window.innerWidth - left);
    const maxHeight = Math.max(0, window.innerHeight - top);
    const w = clampNumber(
      startW + (e.clientX - startX),
      Math.min(MIN_CONSOLE_WIDTH, maxWidth),
      maxWidth,
    );
    const h = clampNumber(
      startH + (e.clientY - startY),
      Math.min(MIN_CONSOLE_HEIGHT, maxHeight),
      maxHeight,
    );
    modal.style.width = w + "px";
    modal.style.height = h + "px";
    clampGeom();
    keepLogsAtBottomAfterResize();
  });
  window.addEventListener("mouseup", () => {
    if (resizing) saveGeom();
    resizing = false;
  });
}

// ─── Visibility ─────────────────────────────────────────

export let visible = false;

export function update(goToGame = true): void {
  devOverlay.style.display = visible ? "block" : "none";
  // Defer focus so the toggling `~` keydown doesn't land in the input.
  if (visible) {
    commitInitialPos();
    clampGeom();
    setTimeout(() => input.focus(), 0);
  } else if (goToGame) input.blur();
}

export function toggleConsole(goToGame = true): void {
  visible = !visible;
  update(goToGame);
  CONTROLS.toggle(!goToGame);
  if (!visible) {
    CONTROLS.locklessChange(true);
    RENDER.renderer.domElement.requestPointerLock();
  }
}

// ─── Server reply mirroring ─────────────────────────────
//
// Tap incoming `ch` packets so command replies (`Server: God mode ON`)
// surface in the console. The compiled engine installs handlers on
// `io.events` once the connection opens; objects.js exposes the `io`
// instance to window. Poll until the table appears, then wrap.

function numberTuple(value: unknown, length: number): number[] | null {
  if (!Array.isArray(value) || value.length < length) return null;
  const tuple = value.slice(0, length).map((part) => Number(part));
  return tuple.every(Number.isFinite) ? tuple : null;
}

function forceCollideKind(source: ForceCollideSourceObject): string {
  const rawKind = source.i ?? source.id ?? "CUBE";
  const kind = String(rawKind).toUpperCase();
  return kind === "0" ? "CUBE" : kind;
}

function sourceHasNoCollision(source: ForceCollideSourceObject): boolean {
  return !!(source.l || source.col);
}

function buildInjectedForceCollideObject(
  source: ForceCollideSourceObject,
  sourceIndex: number,
): ForceCollideInjectedObject | null {
  const position = numberTuple(source.p, 3);
  const size = numberTuple(source.s, 3);
  if (!position || !size) return null;

  const kind = forceCollideKind(source);
  const halfWidth = size[0] / 2;
  const halfHeight = size[1] / 2;
  const halfLength = size[2] / 2;
  const uid = -900000 - sourceIndex;
  const injected: ForceCollideInjectedObject = {
    __forceColInjected: true,
    __forceColSourceIndex: sourceIndex,
    active: true,
    collidable: true,
    noCol: false,
    noVis: true,
    sid: uid,
    uid,
    x: position[0],
    y: position[1] + halfHeight,
    z: position[2],
    width: halfWidth,
    height: halfHeight,
    length: halfLength,
    ter: true,
    type: 0,
    wallJumpable: source.wj == null,
  };

  if (source.bo != null) injected.isBorder = source.bo;
  if (source.pe != null) injected.penetrable = source.pe;

  if (kind === "CYLINDER") {
    const radius = Math.min(size[0], size[2]) / 2;
    injected.collT = "cylinder";
    injected.width = radius;
    injected.length = radius;
    return injected;
  }

  if (kind === "PLANE" || kind === "BILLBOARD" || kind === "RUNE") {
    injected.y = position[1] + (kind === "RUNE" ? 0.01 : 0);
    injected.height = 0;
    injected.width = halfWidth;
    injected.length = halfLength;
    if (kind === "BILLBOARD") injected.type = undefined;
    return injected;
  }

  if (kind === "CUBE" || kind === "CUSTOM_ASSET") {
    if (kind === "CUSTOM_ASSET") injected.complexMesh = true;
    return injected;
  }

  if (
    kind === "POINT_LIGHT" ||
    kind === "SOUND_EMITTER" ||
    kind === "SPECTATE_CAM" ||
    kind === "SIGN" ||
    kind === "LIQUID"
  ) {
    return null;
  }

  return injected;
}

function rebuildForceCollideGrid(manager: AnyRecord): void {
  manager.grids = {};
  if (typeof manager.genQuads === "function") manager.genQuads();
}

function applyBordersFromServer(enabled: boolean): void {
  if (!Game) return;
  Game.config ||= {};
  Game.config.disableB = !enabled;
}

function originalForceCollideObjects(): ForceCollideSourceObject[] {
  const stored = getCurrentOriginalMapData();
  const mapData =
    stored ||
    (Game &&
    Game.map &&
    Array.isArray(Game.map.maps) &&
    Game.map.maps[Game.mapIndex]
      ? Game.map.maps[Game.mapIndex]
      : null);
  return mapData && Array.isArray(mapData.objects)
    ? (mapData.objects as ForceCollideSourceObject[])
    : [];
}

function injectForceCollideObjects(
  manager: AnyRecord,
): ForceCollideInjectedObject[] {
  const runtimeObjects = Array.isArray(manager.objects)
    ? (manager.objects as ForceCollideObject[])
    : [];
  const existingSourceIndexes = new Set(
    runtimeObjects
      .filter((obj) => obj && obj.__forceColInjected)
      .map((obj) => obj.__forceColSourceIndex),
  );
  const injected: ForceCollideInjectedObject[] = [];
  const sourceObjects = originalForceCollideObjects();

  for (let sourceIndex = 0; sourceIndex < sourceObjects.length; sourceIndex++) {
    const source = sourceObjects[sourceIndex];
    if (!source || typeof source !== "object") continue;
    if (!sourceHasNoCollision(source)) continue;
    if (existingSourceIndexes.has(sourceIndex)) continue;
    const obj = buildInjectedForceCollideObject(source, sourceIndex);
    if (!obj) continue;
    runtimeObjects.push(obj);
    injected.push(obj);
  }

  if (injected.length) rebuildForceCollideGrid(manager);
  return injected;
}

function removeInjectedForceCollideObjects(
  manager: AnyRecord,
  injected: ForceCollideInjectedObject[],
): number {
  if (!Array.isArray(manager.objects) || !injected.length) return 0;
  const injectedSet = new Set(injected);
  let removed = 0;
  for (let index = manager.objects.length - 1; index >= 0; index--) {
    if (injectedSet.has(manager.objects[index])) {
      manager.objects.splice(index, 1);
      removed++;
    }
  }
  if (removed) rebuildForceCollideGrid(manager);
  return removed;
}

function applyForceCollideFromServer(enabled: boolean): void {
  const manager = Game && Game.map && Game.map.manager;
  const runtimeObjects =
    manager && Array.isArray(manager.objects) ? manager.objects : [];
  const mapObjects =
    Game &&
    Game.map &&
    Array.isArray(Game.map.maps) &&
    Game.map.maps[Game.mapIndex] &&
    Array.isArray(Game.map.maps[Game.mapIndex].objects)
      ? Game.map.maps[Game.mapIndex].objects
      : [];
  if (runtimeObjects.length === 0 && mapObjects.length === 0) return;

  const state = (manager.__forceColClientState ||
    null) as ForceCollideState | null;
  if (enabled) {
    if (state && state.enabled) return;
    const entries: ForceCollideEntry[] = [];
    for (const obj of [...runtimeObjects, ...mapObjects]) {
      if (!obj || typeof obj !== "object") continue;
      entries.push({
        obj,
        hasNoCol: Object.prototype.hasOwnProperty.call(obj, "noCol"),
        noCol: obj.noCol,
        hasCollidable: Object.prototype.hasOwnProperty.call(obj, "collidable"),
        collidable: obj.collidable,
        hasMeshCollidable:
          !!obj.meshRef &&
          Object.prototype.hasOwnProperty.call(obj.meshRef, "collidable"),
        meshCollidable: obj.meshRef ? obj.meshRef.collidable : undefined,
      });
      if (obj.noCol) obj.noCol = false;
      if (obj.collidable !== true) {
        obj.collidable = true;
      }
      if (obj.meshRef && obj.meshRef.collidable !== true) {
        obj.meshRef.collidable = true;
      }
    }
    const injected = injectForceCollideObjects(manager);
    manager.__forceColClientState = { enabled: true, entries, injected };
    return;
  }

  if (!state || !state.enabled) return;
  for (const entry of state.entries || []) {
    const obj = entry && entry.obj;
    if (!obj) continue;
    if (entry.hasNoCol) obj.noCol = entry.noCol;
    else if (Object.prototype.hasOwnProperty.call(obj, "noCol"))
      delete obj.noCol;

    if (entry.hasCollidable) obj.collidable = entry.collidable;
    else if (Object.prototype.hasOwnProperty.call(obj, "collidable")) {
      delete obj.collidable;
    }

    if (obj.meshRef) {
      if (entry.hasMeshCollidable)
        obj.meshRef.collidable = entry.meshCollidable;
      else if (
        Object.prototype.hasOwnProperty.call(obj.meshRef, "collidable")
      ) {
        delete obj.meshRef.collidable;
      }
    }
  }
  removeInjectedForceCollideObjects(manager, state.injected || []);
  manager.__forceColClientState = null;
}

function hookChat(): void {
  let installed = false;
  const devConsoleKinds: Record<string, true> = {
    log: true,
    info: true,
    warn: true,
    error: true,
    cmd: true,
    reply: true,
    hint: true,
  };
  const handleDevConsolePacket = (
    packet: unknown,
    ...rest: unknown[]
  ): void => {
    if (packet && typeof packet === "object" && !Array.isArray(packet)) {
      const payload = packet as DevConsolePacket;
      const kind =
        typeof payload.kind === "string" && devConsoleKinds[payload.kind]
          ? payload.kind
          : "reply";
      appendLine(
        formatConsoleArgs(Array.isArray(payload.args) ? payload.args : []),
        kind,
      );
      return;
    }

    appendLine(formatConsoleArgs([packet, ...rest]), "reply");
  };
  const wrap = (
    events: Krunker.IOEvents,
    name: string,
    fmt: (...args: any[]) => string | null | undefined,
  ): void => {
    const orig = events[name] as WrappedHandler | undefined;
    if (typeof orig !== "function" || orig.__devConsoleWrapped) return;
    const wrapped = function (this: unknown, ...args: any[]): unknown {
      try {
        const msg = fmt(...args);
        if (msg) append(msg, "reply");
      } catch {}
      return orig.apply(this, arguments);
    };
    wrapped.__devConsoleWrapped = true;
    events[name] = wrapped;
  };
  const onOff = (v: unknown): string => (v ? "Enabled" : "Disabled");
  const tryInstall = (): void => {
    if (installed) return;
    const io = window.io;
    const events = io && io.events;
    if (!events || typeof events.ch !== "function") return;
    wrap(events, "ch", (...packetArgs: unknown[]) => {
      const hasChannel = typeof packetArgs[0] === "number";
      const name = hasChannel ? packetArgs[1] : packetArgs[0];
      const text = hasChannel ? packetArgs[2] : packetArgs[1];
      const cleaned = String(text == null ? "" : text).replace(/<[^>]+>/g, "");
      return `${name || "Server"}: ${cleaned}`;
    });
    wrap(events, "noc", (v) => `Noclip - ${onOff(v)}`);
    wrap(events, "unl", (v) => `Unlimited Ammo - ${onOff(v)}`);
    wrap(events, "fh", (v) => `Force Hide - ${onOff(v)}`);
    const eventTable = events as Krunker.IOEvents & {
      borders?: WrappedHandler;
      devC?: WrappedHandler;
    };
    if (!eventTable.devC || !eventTable.devC.__devConsoleWrapped) {
      const origDevConsole =
        typeof eventTable.devC === "function" ? eventTable.devC : null;
      const wrappedDevConsole = function (
        this: unknown,
        ...packetArgs: unknown[]
      ): unknown {
        try {
          handleDevConsolePacket(packetArgs[0], ...packetArgs.slice(1));
        } catch {}
        if (origDevConsole) return origDevConsole.apply(this, arguments);
      };
      wrappedDevConsole.__devConsoleWrapped = true;
      eventTable.devC = wrappedDevConsole;
    }
    if (!eventTable.borders || !eventTable.borders.__devConsoleWrapped) {
      const origBorders =
        typeof eventTable.borders === "function" ? eventTable.borders : null;
      const wrappedBorders = function (this: unknown, v: unknown): unknown {
        try {
          applyBordersFromServer(!!v);
        } catch {}
        if (origBorders) return origBorders.apply(this, arguments);
      };
      wrappedBorders.__devConsoleWrapped = true;
      eventTable.borders = wrappedBorders;
    }
    // if (!events.forcecol || !events.forcecol.__devConsoleWrapped) {
    //   const origForcecol =
    //     typeof events.forcecol === "function" ? events.forcecol : null;
    //   const wrappedForcecol = function (this: unknown, v: unknown): unknown {
    //     try {
    //       applyForceCollideFromServer(!!v);
    //     } catch {}
    //     if (origForcecol) return origForcecol.apply(this, arguments);
    //   };
    //   wrappedForcecol.__devConsoleWrapped = true;
    //   events.forcecol = wrappedForcecol;
    // }
    installed = true;
  };
  const t = setInterval(() => {
    tryInstall();
    if (installed) clearInterval(t);
  }, 500);
}

hookChat();

function hookOfflineChatCommands(): void {
  const BHOP_MODE_NAMES: Record<number, string> = {
    0: "Disabled",
    1: "Hold Jump/Crouch",
  };

  function resolveBhopMode(arg: unknown, currentMode: number): number | null {
    if (!arg) return currentMode > 0 ? 0 : 1;
    const raw = String(arg).trim().toLowerCase();
    if (["off", "0", "disable", "disabled"].includes(raw)) return 0;
    if (
      [
        "on",
        "1",
        "enable",
        "enabled",
        "hold",
        "manual",
        "held",
        "manualslide",
        "holdslide",
        "jumpcrouch",
        "both",
        "2",
        "3",
        "4",
      ].includes(raw)
    )
      return 1;
    return null;
  }

  function maybeHandleClientBhopCommand(
    label: string,
    payload: unknown,
  ): boolean {
    if (label !== "ct" && label !== "ch") return false;
    if (typeof payload !== "string") return false;
    const line = payload.trim();
    if (!line.toLowerCase().startsWith("/bhop")) return false;

    const rest = line.slice(5).trim();
    const currentMode = window.bhopMode | 0;
    const nextMode = resolveBhopMode(rest, currentMode);
    if (nextMode == null) {
      append("Usage: /bhop [on|off|held]", "error");
      return true;
    }

    window.bhopMode = nextMode;
    window.bhopEnabled = nextMode > 0;
    try {
      localStorage.setItem("bhop", String(nextMode));
    } catch {}
    const msg = `Bhop - ${BHOP_MODE_NAMES[nextMode] || "Hold Jump/Crouch"}`;
    append(msg, "reply");
    try {
      if (typeof window.notify === "function") window.notify(msg);
    } catch {}
    return true;
  }

  let installed = false;
  const tryInstall = (): void => {
    if (installed) return;
    const io = window.io;
    if (!io || typeof io.send !== "function") return;
    if ((io.send as any).__devConsoleOfflineWrapped) {
      installed = true;
      return;
    }
    const origSend = io.send;
    io.send = function (
      this: Krunker.IO,
      label: string,
      ...data: any[]
    ): unknown {
      if (maybeHandleClientBhopCommand(label, data[0])) {
        return;
      }
      if ((label === "ct" || label === "devC") && this && this.socket === null) {
        const line = data[0];
        if (typeof line === "string" && maybeRunOfflineServerCommand(line)) {
          return;
        }
      }
      return origSend.apply(this, arguments as any);
    };
    (io.send as any).__devConsoleOfflineWrapped = true;
    installed = true;
  };
  const t = setInterval(() => {
    tryInstall();
    if (installed) clearInterval(t);
  }, 500);
}

hookOfflineChatCommands();

// ─── Key bind dispatch ──────────────────────────────────
//
// Source-style `bind` runs the bound command on keydown. We only
// fire when the console is closed and focus isn't in another input.

document.addEventListener("keydown", (e) => {
  if (e.repeat) return;
  if (visible) return;
  const tag = e.target instanceof Element ? e.target.tagName : "";
  if (tag === "INPUT" || tag === "TEXTAREA") return;
  const cmd = keyBinds[e.key] || keyBinds[e.code];
  if (cmd) {
    e.preventDefault();
    exec(cmd);
  }
});

// ─── Bootstrap banner ───────────────────────────────────

append("Krunker developer console - type `help` for commands.", "info");
append("Press ~ to toggle, Esc to close, Tab to autocomplete.", "hint");

// Expose for devtools.
// window.devConsole = {
//   exec,
//   print,
//   toggleConsole,
//   update,
//   get visible() {
//     return visible;
//   },
//   set visible(value) {
//     visible = !!value;
//   },
// };
